/* assets/main-search.js — GLOBAL-SEARCH-RUNTIME-22
 * Canonical Owner:
 * - Search Engine Context & Dispatch (window.LittleGSearchEngine)
 * - Command Palette / Global Search Overlay ([data-global-search-overlay])
 * - Search Mode Contract (business_location, home_search, planning_change, place_risk)
 * - Search History & Predictive Search Custom Elements
 * - Place Diagnosis & Result Workspace
 * - Local surface suggestion adapter registry (no second search engine)
 *
 * Exclusions:
 * - Does NOT own Home Composer DOM, Typewriter, Hot Demos, or Entrance Cards (owned by assets/littleg-opening-gate.js).
 */
/**
 * @typedef {Window & typeof globalThis & {
 *   __LITTLEG_GLOBAL_SEARCH_RUNTIME__?: string,
 *   __LITTLEG_GLOBAL_SEARCH_SHORTCUT_BOUND__?: string,
 *   LittleGSearchEngine?: object,
 *   routes?: {predictive_search_url?: string},
 *   dataLayer?: {push?: (value: Record<string, unknown>) => unknown}
 * }} SearchBrowserWindow
 */
((/** @type {SearchBrowserWindow} */ window) => {
  'use strict';

  /**
   * @typedef {'business_location' | 'home_search' | 'planning_change' | 'place_risk'} ScenarioKey
   * @typedef {'understand_place' | 'understand_area' | 'assess_suitability' | 'understand_value' | 'explore_possibilities'} ConsumerJob
   * @typedef {'PLACE' | 'AROUND' | 'FIT' | 'VALUE' | 'CHANGE'} PresentationKey
   * @typedef {'situation' | 'assessment' | 'evidence' | 'missing' | 'action'} ResultModule
   * @typedef {{intent?: string, inputMode?: string, placeId?: string, placeLabel?: string, returnUrl?: string}} SearchContextOptions
   * @typedef {{query: string, intent: string, source: string, inputMode: string, placeId: string, placeLabel: string, returnUrl: string, scenario?: string, surface_mode: string}} SearchContext
   * @typedef {(query: string, context: Readonly<Record<string, unknown>>) => unknown[] | Promise<unknown[]>} SuggestionAdapter
   * @typedef {string | {message?: unknown, label?: unknown} | null | undefined} GapValue
   * @typedef {{state: string, items: {text: string, state: string, source: string}[]}} PlaceContext
   * @typedef {{state?: unknown, summary?: unknown, source_ids?: unknown[]}} EvidenceFinding
   * @typedef {{id?: unknown, provider_name?: unknown, provider?: unknown, as_of?: unknown, checked_at?: unknown, retrieved_at?: unknown}} EvidenceSource
   * @typedef {{label?: unknown, name?: unknown, value?: unknown, period?: unknown, provider_name?: unknown, provider?: unknown}} MarketItem
   * @typedef {{
   *   consumer_job?: unknown,
   *   evidence?: {findings?: EvidenceFinding[], sources?: EvidenceSource[], missing?: GapValue[]},
   *   fit_response?: {contract?: unknown, scenario?: unknown, surface_mode?: unknown, known_facts?: unknown[], missing_blindspots?: unknown[], actionable_next_steps?: unknown[]},
   *   missing_inputs?: GapValue[],
   *   recommended_next_step?: GapValue,
   *   readiness?: {missing_inputs?: GapValue[], recommended_next_step?: GapValue},
   *   market_context?: {market_context_ready?: unknown, valuation_ready?: unknown, opinion_of_value?: unknown, latest_period?: unknown, items?: MarketItem[]}
   * }} StructuredResult
   * @typedef {{
   *   state?: string, request_id?: unknown, retryable?: boolean, code?: string, action?: string,
   *   consumer_job?: unknown, evidence_state?: unknown,
   *   place?: {formatted_address?: unknown, label?: unknown, name?: unknown, text?: unknown, verified?: unknown, state?: unknown},
   *   place_resolution?: {state?: unknown, candidates?: {label?: unknown, formatted_address?: unknown, name?: unknown}[]},
   *   structured_result?: StructuredResult
   * }} SpatialData
   * @typedef {{success?: unknown, data?: SpatialData, error?: {retryable?: unknown, code?: unknown, action?: unknown}, request_id?: unknown, meta?: {request_id?: unknown}}} SpatialEnvelope
   * @typedef {MainSearch & {__littlegPlaceResultControllers?: Set<AbortController>, __littlegPlaceResultToken?: string, __littlegPlaceResultBound?: boolean}} SearchOwner
   * @typedef {ReturnType<typeof placeCopy>} PlaceCopy
   * @typedef {ReturnType<typeof projectPlaceEvidence>} PlaceProjection
   */

  const VERSION = '2026-10-13-GLOBAL-SEARCH-22';
  const HISTORY_KEY = 'littleg:global-search-history:v1';
  const HISTORY_LIMIT = 8;
  const OVERLAY_ID = 'GlobalSearchOverlay';
  const OVERLAY_SELECTOR = '[data-global-search-overlay]';
  const SEARCH_TRIGGER_SELECTOR = '[data-global-search-trigger]';
  const SEARCH_OPEN_CLASS = 'is-search-open';
  const RESULT_TABS = new Set(['all', 'diagnosis', 'tools', 'articles']);
  const SEARCH_SURFACE_MODE = 'search';

  const SPATIAL_API_CONTRACT = Object.freeze({
    api_family: 'spatial',
    canonical_endpoint: 'https://api.glhksolution.com/api/spatial/entry',
    method: 'POST',
    request_contract: 'littleg_spatial_intent_v1',
    response_contract: 'littleg_spatial_entry_result_v1',
  });

  const SEARCH_MECHANISMS = Object.freeze({
    ai_qna: 'ai_qna',
    backend_authority: 'backend_authority',
    product_content: 'product_content',
    filter_faceted: 'filter_faceted',
    launcher_utility: 'launcher_utility',
  });
  const PLATFORM_SEARCH_INTENTS = new Set([
    'place_check',
    'location_search',
    'opportunity_search',
    'provider_search',
    'project_search',
    'family_explore',
    'content_search',
    'explore_space',
    'tool_execution',
  ]);

  const CANONICAL_CONSUMER_JOBS = new Set([
    'understand_place',
    'understand_area',
    'assess_suitability',
    'understand_value',
    'explore_possibilities',
  ]);

  const PRESENTATION_KEY_BY_CONSUMER_JOB = Object.freeze({
    understand_place: 'PLACE',
    understand_area: 'AROUND',
    assess_suitability: 'FIT',
    understand_value: 'VALUE',
    explore_possibilities: 'CHANGE',
  });

  const SCENARIO_REQUEST_PROFILES = Object.freeze({
    business_location: Object.freeze({
      key: 'business_location',
      consumer_job: 'assess_suitability',
    }),
    home_search: Object.freeze({ key: 'home_search', consumer_job: 'assess_suitability' }),
    planning_change: Object.freeze({
      key: 'planning_change',
      consumer_job: 'explore_possibilities',
    }),
    place_risk: Object.freeze({ key: 'place_risk', consumer_job: 'understand_place' }),
  });

  const SCENARIOS = new Set(Object.keys(SCENARIO_REQUEST_PROFILES));
  const PLACE_RESULT_WORKSPACE_CONTRACT = 'littleg_result_workspace_v1';
  const PLACE_FIT_RESPONSE_CONTRACT = 'littleg_fit_diagnosis_v1';
  const PLACE_REQUEST_TIMEOUT_MS = 7000;
  /** @type {readonly ResultModule[]} */
  const PLACE_RESULT_MODULES = Object.freeze([
    'situation',
    'assessment',
    'evidence',
    'missing',
    'action',
  ]);
  const TECHNICAL_COPY_PATTERNS = Object.freeze([
    /\b(?:api|endpoint|payload|contract|engine|runtime|module|lens|surface(?:_mode)?|execution(?:_owner)?|canonical|diagnostic|diagnosis)\b/i,
    /\b(?:consumer[_ -]?job|evidence[_ -]?mode|worker|service binding|d1|r2|webhook|json|liquid|selector|provider|gemini|openai)\b/i,
  ]);
  const TECHNICAL_COPY_PATTERN = Object.freeze({
    /** @param {string} text */
    test(text) {
      return TECHNICAL_COPY_PATTERNS.some((pattern) => pattern.test(text));
    },
  });

  if (window.__LITTLEG_GLOBAL_SEARCH_RUNTIME__ === VERSION) return;
  window.__LITTLEG_GLOBAL_SEARCH_RUNTIME__ = VERSION;

  const SELECTORS = Object.freeze({
    searchInput: 'input[type="search"]',
    canonicalSearchInput: '[data-canonical-search-input]',
    resetButton: '[data-search-reset]',
    predictiveResults: '[data-predictive-search]',
    predictiveSearchText: '[data-predictive-search-search-for-text]',
    predictiveCount: '[data-predictive-search-live-region-count-value]',
    selected: '[role="option"][aria-selected="true"]',
    selectable: '[role="option"]',
    selectedAction:
      '[role="option"][aria-selected="true"] a, [role="option"][aria-selected="true"] button',
    history: '[data-search-history]',
    historyList: '[data-search-history-list]',
    historyClear: '[data-search-history-clear]',
    searchAll: '[data-search-all]',
  });

  let isDispatching = false;
  /** @type {Element | null} */
  let activeSearchTrigger = null;

  /** @param {unknown} value */
  const clean = (value) =>
    String(value == null ? '' : value)
      .replace(/\s+/g, ' ')
      .trim();
  /** @param {unknown} value */
  const escapeRegExp = (value) => String(value).replace(/[.*+?^${}()|[\]\\]/g, String.raw`\$&`);
  const createAbortController = () =>
    typeof AbortController === 'undefined' ? null : new AbortController();
  /**
   * @param {string} type
   * @param {EventInit} [options]
   */
  const createEvent = (type, options = {}) => {
    if (typeof Event === 'function') return new Event(type, options);
    const event = document.createEvent('Event');
    // Retain the compatibility path for browsers without the Event constructor.
    event.initEvent(type, Boolean(options.bubbles), Boolean(options.cancelable));
    return event;
  };
  /** @param {NodeListOf<HTMLInputElement> | HTMLInputElement[]} inputs */
  const uniqueFormsFromInputs = (inputs) => {
    /** @type {HTMLFormElement[]} */
    const forms = [];
    inputs.forEach((input) => {
      if (input.form && !forms.includes(input.form)) forms.push(input.form);
    });
    return forms;
  };
  const localeRoot = () => {
    let root = clean(document.body.dataset.localeRoot);
    if (!root || root === '/') return '';
    if (!root.startsWith('/')) root = `/${root}`;
    let end = root.length;
    while (end > 0 && root[end - 1] === '/') end -= 1;
    return root.slice(0, end);
  };
  /** @param {unknown} path */
  const localizedPath = (path) => {
    const target = String(path || '/').startsWith('/') ? String(path || '/') : `/${path}`;
    const root = localeRoot();
    if (!root || target === root || target.startsWith(`${root}/`)) return target;
    return `${root}${target}`;
  };
  /** @param {unknown} value */
  const normalizeHistoryTerm = (value) =>
    String(value || '')
      .replace(/\s+/g, ' ')
      .trim()
      .slice(0, 120);
  const safeStorageRead = () => {
    try {
      const value = JSON.parse(window.localStorage.getItem(HISTORY_KEY) || '[]');
      return Array.isArray(value)
        ? value.filter((item) => typeof item === 'string' && item.trim()).slice(0, HISTORY_LIMIT)
        : [];
    } catch {
      // Unavailable or malformed history is treated as empty so search remains usable.
      return [];
    }
  };
  /** @param {string[]} items */
  const safeStorageWrite = (items) => {
    try {
      window.localStorage.setItem(HISTORY_KEY, JSON.stringify(items.slice(0, HISTORY_LIMIT)));
    } catch {
      // Storage may be blocked or full; history persistence must not block search.
    }
  };
  /** @param {unknown} value */
  const rememberHistory = (value) => {
    const term = normalizeHistoryTerm(value);
    if (!term) return [];
    const current = safeStorageRead();
    const next = [
      term,
      ...current.filter((item) => item.toLowerCase() !== term.toLowerCase()),
    ].slice(0, HISTORY_LIMIT);
    safeStorageWrite(next);
    window.dispatchEvent(
      new CustomEvent('littleg:search:history-updated', {
        detail: { key: HISTORY_KEY, items: next.slice() },
      })
    );
    return next;
  };

  /** @param {unknown} value */
  const normalizeScenario = (value) => {
    const scenario = clean(value).toLowerCase();
    return SCENARIOS.has(scenario) ? /** @type {ScenarioKey} */ (scenario) : '';
  };
  /** @param {unknown} value */
  const scenarioProfile = (value) => {
    const scenario = normalizeScenario(value);
    return scenario ? SCENARIO_REQUEST_PROFILES[scenario] : null;
  };
  /** @param {unknown} value */
  const normalizeConsumerJob = (value) => {
    const job = clean(value).toLowerCase().replaceAll('-', '_').replace(/\s+/g, '_');
    return CANONICAL_CONSUMER_JOBS.has(job) ? /** @type {ConsumerJob} */ (job) : '';
  };
  /** @param {unknown} value */
  const presentationKeyForConsumerJob = (value) => {
    const job = normalizeConsumerJob(value);
    return job ? PRESENTATION_KEY_BY_CONSUMER_JOB[job] : 'PLACE';
  };
  /** @param {SpatialData | null | undefined} data */
  const consumerJobFromData = (data) =>
    normalizeConsumerJob(data?.consumer_job || data?.structured_result?.consumer_job);
  const currentScenario = () => {
    try {
      const params = new URLSearchParams(window.location.search || '');
      const direct = normalizeScenario(params.get('scenario'));
      if (direct) return direct;
      return normalizeScenario(params.get('surface_mode'));
    } catch {
      // Invalid location parameters must fall back to the default search scenario.
      return '';
    }
  };

  /** @param {unknown} query */
  const detectCanonicalIntent = (query) => {
    const value = clean(query).toLowerCase();
    if (
      ['招标', '招標', 'gtender', 'tender', '资助', '資助', '竞赛', '競賽', 'opportunity'].some(
        (keyword) => value.includes(keyword)
      )
    )
      return 'opportunity_search';
    if (
      ['供应商', '合作夥伴', '合作方', 'provider', 'supplier', 'capability', '能力'].some(
        (keyword) => value.includes(keyword)
      )
    )
      return 'provider_search';
    if (
      [
        '亲子',
        '親子',
        '儿童',
        '兒童',
        '小朋友',
        'family',
        'kids',
        'children',
        'nearby activity',
        '附近活动',
        '附近活動',
      ].some((keyword) => value.includes(keyword))
    )
      return 'family_explore';
    if (
      ['address', '地址', 'street', '街道', 'building', '大厦', '大廈', 'estate', '屋苑'].some(
        (keyword) => value.includes(keyword)
      )
    )
      return 'location_search';
    if (
      ['product', '产品', '產品', 'article', '文章', 'collection', '商品'].some((keyword) =>
        value.includes(keyword)
      )
    )
      return 'content_search';
    if (['project', '项目', '項目'].some((keyword) => value.includes(keyword)))
      return 'project_search';
    if (
      ['map', 'gis', '3d', '图层', '圖層', '地图', '地圖', 'explore', 'layer'].some((keyword) =>
        value.includes(keyword)
      )
    )
      return 'explore_space';
    if (
      ['workflow', 'toolkit', 'tool', '分析工具', '报批', '報批'].some((keyword) =>
        value.includes(keyword)
      )
    )
      return 'tool_execution';
    return 'place_check';
  };
  /** @param {unknown} query */
  const placeOptionsForCurrentQuery = (query) => {
    const params = new URLSearchParams(window.location.search || '');
    const currentQuery = normalizeHistoryTerm(params.get('q'));
    const nextQuery = normalizeHistoryTerm(query);

    if (!currentQuery || nextQuery !== currentQuery) {
      return {};
    }

    return {
      placeId: clean(params.get('place_id')),
      placeLabel: clean(params.get('place_label')),
    };
  };

  /**
   * @param {unknown} query
   * @param {string} [source]
   * @param {SearchContextOptions} [options]
   */
  const createCanonicalContext = (query, source = 'home-composer', options = {}) => ({
    query: normalizeHistoryTerm(query),
    intent: clean(options.intent) || detectCanonicalIntent(query),
    source: clean(source) || 'home-composer',
    inputMode: clean(options.inputMode) || 'text',
    placeId: clean(options.placeId),
    placeLabel: clean(options.placeLabel),
    returnUrl: clean(options.returnUrl) || window.location.pathname + window.location.search,
    scenario:
      clean(options.intent) === 'content_search' ? undefined : currentScenario() || undefined,
    surface_mode: SEARCH_SURFACE_MODE,
  });
  /** @param {Partial<SearchContext> | null | undefined} context */
  const dispatchCanonicalContext = (context) => {
    if (!context || !clean(context.query) || isDispatching) return false;
    const query = normalizeHistoryTerm(context.query);
    const requestedIntent = clean(context.intent);
    const scenario =
      requestedIntent === 'content_search'
        ? ''
        : normalizeScenario(context.scenario || context.surface_mode);
    const intent = scenario ? 'place_check' : requestedIntent || detectCanonicalIntent(query);
    const source = clean(context.source) || 'global-search';
    isDispatching = true;
    window.setTimeout(() => {
      isDispatching = false;
    }, 1200);
    const detail = Object.freeze({
      query,
      intent,
      source,
      inputMode: clean(context.inputMode) || 'text',
      placeId: clean(context.placeId),
      placeLabel: clean(context.placeLabel),
      returnUrl: clean(context.returnUrl) || window.location.pathname + window.location.search,
      surface_mode: SEARCH_SURFACE_MODE,
      scenario: scenario || undefined,
    });
    rememberHistory(query);
    window.dispatchEvent(new CustomEvent('littleg:search:dispatch', { detail }));
    const params = new URLSearchParams({ q: query, intent, source });
    params.set('surface_mode', SEARCH_SURFACE_MODE);
    if (scenario) params.set('scenario', scenario);

    if (detail.placeId) {
      params.set('place_id', detail.placeId);
    }
    if (detail.placeLabel) {
      params.set('place_label', detail.placeLabel);
    }

    window.location.assign(`${localizedPath('/search')}?${params.toString()}`);
    return true;
  };

  /** @param {boolean} open */
  const syncSearchTriggers = (open) =>
    document.querySelectorAll(SEARCH_TRIGGER_SELECTOR).forEach((trigger) => {
      trigger.setAttribute('aria-controls', OVERLAY_ID);
      trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
    });
  /** @param {boolean} open */
  const setSearchOpenClass = (open) => {
    document.documentElement.classList.toggle(SEARCH_OPEN_CLASS, open);
    document.body?.classList.toggle(SEARCH_OPEN_CLASS, open);
  };
  /** @param {HTMLElement | null} overlay */
  const overlayFocusable = (overlay) => {
    /** @type {NodeListOf<HTMLElement> | undefined} */
    const nodes = overlay?.querySelectorAll(
      'input:not([disabled]), button:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])'
    );
    return Array.from(nodes || []).filter(
      (node) =>
        !node.hidden &&
        node.getAttribute('aria-hidden') !== 'true' &&
        node.getClientRects().length > 0
    );
  };
  const closeGlobalSearch = (restoreFocus = true) => {
    /** @type {HTMLElement | null} */
    const overlay = document.querySelector(OVERLAY_SELECTOR);
    if (!overlay || overlay.hidden) {
      setSearchOpenClass(false);
      syncSearchTriggers(false);
      return false;
    }
    overlay.setAttribute('aria-hidden', 'true');
    overlay.dataset.searchState = 'closed';
    overlay.hidden = true;
    setSearchOpenClass(false);
    syncSearchTriggers(false);
    const restore = activeSearchTrigger;
    activeSearchTrigger = null;
    if (
      restoreFocus &&
      restore &&
      document.contains(restore) &&
      'focus' in restore &&
      typeof restore.focus === 'function'
    )
      restore.focus();
    return true;
  };
  const openCommandPalette = (source = 'global') => {
    /** @type {HTMLElement | null} */
    const overlay = document.querySelector(OVERLAY_SELECTOR);
    if (!overlay) return false;
    activeSearchTrigger =
      document.activeElement && document.activeElement !== document.body
        ? document.activeElement
        : document.querySelector(SEARCH_TRIGGER_SELECTOR);
    document.dispatchEvent(
      new CustomEvent('littleg:overlay:close-others', {
        detail: { except: 'global-search', source: VERSION },
      })
    );
    overlay.hidden = false;
    overlay.setAttribute('aria-hidden', 'false');
    overlay.dataset.searchState = 'open';
    overlay.dataset.searchSource = clean(source) || 'global';
    setSearchOpenClass(true);
    syncSearchTriggers(true);
    /** @type {HTMLInputElement | null} */
    const overlayInput = overlay.querySelector(
      'input[data-command-palette-input], input[type="search"]'
    );
    window.requestAnimationFrame(() => {
      overlayInput?.focus();
      overlayInput?.select?.();
    });
    return true;
  };
  const focusCurrentSearchSurface = () => {
    /** @type {HTMLInputElement | null} */
    const input = document.querySelector(
      'main-search[data-result-workspace-contract] input[type="search"]'
    );
    if (!input) return false;
    input.scrollIntoView({ behavior: 'smooth', block: 'center' });
    input.focus({ preventScroll: true });
    input.select?.();
    return true;
  };

  const openGlobalSearch = (source = 'global') => {
    if (openCommandPalette(source)) return true;
    return focusCurrentSearchSurface();
  };

  /** @param {Element | null} node */
  const isEditableTarget = (node) =>
    Boolean(
      node &&
      ((node instanceof HTMLElement && node.isContentEditable) ||
        node.matches?.('input, textarea, [contenteditable=""], [contenteditable="true"]'))
    );
  /** @param {Element | null} node */
  const isCanonicalSearchTarget = (node) =>
    Boolean(
      node?.matches?.(
        '[data-composer-input], [data-need-input], [data-command-palette-input], main-search input[type="search"], predictive-search input[type="search"]'
      )
    );
  /**
   * @param {KeyboardEvent} event
   * @param {HTMLElement} overlay
   */
  const trapOverlayFocus = (event, overlay) => {
    if (event.key !== 'Tab') return;
    const items = overlayFocusable(overlay);
    if (!items.length) {
      event.preventDefault();
      return;
    }
    const first = items[0];
    const last = items.at(-1);
    if (!first || !last) return;
    const active = document.activeElement;
    if (!overlay.contains(active)) {
      event.preventDefault();
      (event.shiftKey ? last : first).focus();
    } else if (event.shiftKey && active === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  };

  const LOCAL_SUGGESTION_ADAPTERS = new Map();
  /** @param {unknown} value */
  const suggestionAdapterKey = (value) => {
    const key = clean(value)
      .toLowerCase()
      .replace(/[^a-z0-9_-]+/g, '-');
    let start = 0;
    let end = key.length;
    while (start < end && key[start] === '-') start += 1;
    while (end > start && key[end - 1] === '-') end -= 1;
    return key.slice(start, end).slice(0, 64);
  };
  /**
   * @param {unknown} name
   * @param {SuggestionAdapter} adapter
   */
  const registerSuggestionAdapter = (name, adapter) => {
    const key = suggestionAdapterKey(name);
    if (!key || typeof adapter !== 'function') return false;
    LOCAL_SUGGESTION_ADAPTERS.set(key, adapter);
    return true;
  };
  /** @param {unknown} name */
  const unregisterSuggestionAdapter = (name) => {
    const key = suggestionAdapterKey(name);
    return !!key && LOCAL_SUGGESTION_ADAPTERS.delete(key);
  };
  /**
   * @param {unknown} query
   * @param {Readonly<Record<string, unknown>>} [context]
   */
  const collectSuggestionAdapterResults = async (query, context = {}) => {
    const term = normalizeHistoryTerm(query);
    if (!term) return [];
    /** @type {(object & {adapter: string, adapterIndex: number})[]} */
    const output = [];
    // Sequential execution preserves registration order and adapter precedence.
    for (const [adapterName, adapter] of LOCAL_SUGGESTION_ADAPTERS.entries()) {
      try {
        const rows = await adapter(term, Object.freeze({ ...context, adapter: adapterName }));
        (Array.isArray(rows) ? rows : []).forEach((row, index) => {
          if (!row || typeof row !== 'object') return;
          output.push(Object.freeze({ ...row, adapter: adapterName, adapterIndex: index }));
        });
      } catch {
        // Optional adapter failures are isolated; continue in the existing registration order.
      }
    }
    return output;
  };

  window.LittleGSearchEngine = Object.freeze({
    version: VERSION,
    historyKey: HISTORY_KEY,
    api: SPATIAL_API_CONTRACT,
    mechanisms: SEARCH_MECHANISMS,
    detectIntent: detectCanonicalIntent,
    createContext: createCanonicalContext,
    dispatchContext: dispatchCanonicalContext,
    open: openGlobalSearch,
    close: closeGlobalSearch,
    suggestionAdapters: Object.freeze({
      register: registerSuggestionAdapter,
      unregister: unregisterSuggestionAdapter,
      collect: collectSuggestionAdapterResults,
    }),
  });

  const bindGlobalShortcut = () => {
    if (window.__LITTLEG_GLOBAL_SEARCH_SHORTCUT_BOUND__ === VERSION) return;
    window.__LITTLEG_GLOBAL_SEARCH_SHORTCUT_BOUND__ = VERSION;
    document.addEventListener('keydown', (event) => {
      /** @type {HTMLElement | null} */
      const overlay = document.querySelector(OVERLAY_SELECTOR);
      const overlayOpen = Boolean(
        overlay && !overlay.hidden && overlay.getAttribute('aria-hidden') !== 'true'
      );
      if (overlayOpen && overlay) {
        if (event.key === 'Escape') {
          event.preventDefault();
          closeGlobalSearch(true);
          return;
        }
        if (event.key === 'Tab') {
          trapOverlayFocus(event, overlay);
          return;
        }
      }
      if (
        event.defaultPrevented ||
        event.altKey ||
        !(event.metaKey || event.ctrlKey) ||
        String(event.key || '').toLowerCase() !== 'k'
      )
        return;
      const active = document.activeElement;
      if (isEditableTarget(active) && !isCanonicalSearchTarget(active)) return;
      event.preventDefault();
      openGlobalSearch('command-k');
    });
    document.addEventListener('submit', (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;
      if (!form.matches?.('[data-global-search-form]')) return;
      if (event.defaultPrevented) return;

      /** @type {HTMLInputElement | null} */
      const input = form.querySelector('[data-command-palette-input]');
      const query = normalizeHistoryTerm(input?.value);

      event.preventDefault();

      if (!query) {
        input?.focus();
        return;
      }

      dispatchCanonicalContext(createCanonicalContext(query, 'global-command'));
    });

    document.addEventListener('click', (event) => {
      if (!(event.target instanceof Element)) return;
      const close = event.target.closest?.('[data-global-search-close]');
      if (close) {
        event.preventDefault();
        closeGlobalSearch(true);
        return;
      }
      const trigger = event.target.closest?.(SEARCH_TRIGGER_SELECTOR);
      if (trigger) {
        event.preventDefault();
        activeSearchTrigger = trigger;
        openGlobalSearch('trigger-click');
      }
    });
    document.addEventListener('littleg:search:open', (event) =>
      openGlobalSearch(
        /** @type {CustomEvent<{source?: string}>} */ (event).detail?.source || 'external'
      )
    );
    window.addEventListener('littleg:command-palette:open', (event) => {
      openGlobalSearch(
        /** @type {CustomEvent<{source?: string}>} */ (event).detail?.source || 'external'
      );
    });
    document.addEventListener('littleg:overlay:close-others', (event) => {
      if (/** @type {CustomEvent<{except?: string}>} */ (event).detail?.except !== 'global-search')
        closeGlobalSearch(false);
    });
    window.addEventListener('pageshow', () => closeGlobalSearch(false));
    syncSearchTriggers(false);
  };

  class CoreSearchForm extends HTMLElement {
    constructor() {
      super();
      /** @type {HTMLInputElement | null} */
      this.input = this.querySelector(SELECTORS.searchInput);
      /** @type {HTMLElement | null} */
      this.resetButton = this.querySelector(SELECTORS.resetButton);
      this.controlOwner =
        this.input?.closest('search-form, main-search, predictive-search') || null;
      if (this.input && this.controlOwner === this) {
        this.input.addEventListener('input', this.onInput.bind(this));
        this.input.form?.addEventListener('reset', this.onFormReset.bind(this));
      }
      if (this.resetButton && this.controlOwner === this)
        this.resetButton.addEventListener('click', this.onReset.bind(this));
      this.updateResetState();
    }
    /** @param {Event} _event */
    onInput(_event) {
      this.updateResetState();
    }
    /** @param {Event} event */
    onReset(event) {
      event?.preventDefault();
      if (!this.input) return;
      this.input.value = '';
      this.updateResetState();
      this.input.dispatchEvent(createEvent('input', { bubbles: true }));
      this.input.focus();
    }
    /** @param {Event} _event */
    onFormReset(_event) {
      window.requestAnimationFrame(() => this.updateResetState());
    }
    updateResetState() {
      if (!this.resetButton || !this.input) return;
      const hidden = this.input.value === '';
      this.resetButton.hidden = hidden;
      this.resetButton.classList.toggle('hidden', hidden);
    }
    shouldResetForm() {
      return !document.activeElement?.closest('form');
    }
  }
  if (!customElements.get('search-form')) customElements.define('search-form', CoreSearchForm);

  class MainSearch extends CoreSearchForm {
    constructor() {
      super();
      /** @type {HTMLFormElement | null} */
      this.form = this.querySelector('form.main-search-form');
      /** @type {HTMLElement | null} */
      this.searchSection = this.closest('[data-module="main-search"]');
      /** @type {NodeListOf<HTMLInputElement>} */
      this.allSearchInputs = document.querySelectorAll(SELECTORS.canonicalSearchInput);
      /** @type {HTMLElement | null} */
      this.historyPanel = this.querySelector(SELECTORS.history);
      /** @type {HTMLElement | null} */
      this.historyList = this.querySelector(SELECTORS.historyList);
      /** @type {HTMLButtonElement | null} */
      this.historyClearButton = this.querySelector(SELECTORS.historyClear);
      this.history = safeStorageRead();
      this.enableTracking = this.dataset.enableTracking === 'true';
      /** @type {HTMLElement[]} */
      this.tabs = [];
      /** @type {HTMLElement[]} */
      this.quickPills = [];
      /** @type {HTMLElement[]} */
      this.resultItems = [];
      /** @type {HTMLElement | null} */
      this.diagnosisSurface = null;
      /** @type {HTMLElement | null} */
      this.emptyState = null;
      /** @type {HTMLElement | null} */
      this.resultsHeader = null;
      /** @type {HTMLElement | null} */
      this.visibleCount = null;
      this.activeResultTab = 'all';
      if (!this.input || !this.form) return;
      this.setupEventListeners();
      this.initializeResultTabs();
      this.syncQuickPills(currentScenario());
      this.renderHistory();
    }
    setupEventListeners() {
      if (!this.input || !this.form) return;
      this.input.addEventListener('focus', this.onInputFocus.bind(this));
      this.input.addEventListener('input', this.onMainInput.bind(this));
      this.form.addEventListener('submit', this.onSubmit.bind(this));
      this.form.addEventListener('click', this.onDelegatedClick.bind(this));
      this.searchSection?.addEventListener('click', this.onResultTabClick.bind(this));
      this.searchSection?.addEventListener('keydown', this.onResultTabKeydown.bind(this));
      uniqueFormsFromInputs(this.allSearchInputs).forEach((form) => {
        if (form !== this.form) form.addEventListener('reset', this.onFormReset.bind(this));
      });
      this.allSearchInputs.forEach((input) => {
        if (input !== this.input) input.addEventListener('input', this.onExternalInput.bind(this));
      });
      this.historyClearButton?.addEventListener('click', this.clearHistory.bind(this));
      window.addEventListener('littleg:search:history-updated', () => {
        this.history = safeStorageRead();
        this.renderHistory();
      });
      /** @type {NodeListOf<HTMLElement>} */
      const searchActions = this.querySelectorAll('[data-search-action]');
      searchActions.forEach((element) => {
        element.addEventListener('click', () => {
          this.pushEvent(element.dataset.searchAction || 'click', {
            href: element.getAttribute('href') || '',
            query: this.searchTerms(),
          });
          const action = element.dataset.searchAction || '';
          if (
            ![
              'place-analysis',
              'projects-radar',
              'gproperty-family-fit',
              'gproperty-owner-fit',
            ].includes(action)
          )
            return;
          const query = this.searchTerms();
          if (query)
            element.setAttribute(
              'href',
              this.buildUrl(element.getAttribute('href') || '', { q: query, source: 'main-search' })
            );
        });
      });
    }
    refreshResultControls() {
      /** @type {HTMLElement[]} */
      this.tabs = Array.from(this.searchSection?.querySelectorAll('[data-search-tab]') || []);
      /** @type {HTMLElement[]} */
      this.quickPills = Array.from(this.form?.querySelectorAll('[data-search-quick-mode]') || []);
      /** @type {HTMLElement[]} */
      this.resultItems = Array.from(
        this.searchSection?.querySelectorAll('.msearch__result-item[data-search-result-kind]') || []
      );
      /** @type {HTMLElement | null} */
      this.diagnosisSurface =
        this.searchSection?.querySelector('[data-search-diagnosis-surface]') || null;
      /** @type {HTMLElement | null} */
      this.emptyState = this.searchSection?.querySelector('[data-search-empty-state]') || null;
      /** @type {HTMLElement | null} */
      this.resultsHeader =
        this.searchSection?.querySelector('[data-search-results-header]') || null;
      /** @type {HTMLElement | null} */
      this.visibleCount = this.searchSection?.querySelector('[data-visible-result-count]') || null;
    }
    /** @param {unknown} [value] */
    syncQuickPills(value = currentScenario()) {
      /** @type {HTMLElement[]} */
      this.quickPills = Array.from(this.form?.querySelectorAll('[data-search-quick-mode]') || []);
      const scenario = normalizeScenario(value);
      this.quickPills.forEach((button) =>
        button.setAttribute(
          'aria-pressed',
          button.dataset.searchQuickMode === scenario ? 'true' : 'false'
        )
      );
    }
    /** @param {unknown} value */
    activateQuickMode(value) {
      const scenario = normalizeScenario(value);
      const profile = scenarioProfile(scenario);
      const query = this.searchTerms();
      if (!scenario || !profile || !query) return false;
      try {
        const url = new URL(window.location.href);
        url.searchParams.set('q', query);
        url.searchParams.set('intent', 'place_check');
        url.searchParams.set('source', 'search-quick');
        url.searchParams.set('surface_mode', SEARCH_SURFACE_MODE);
        url.searchParams.set('scenario', scenario);
        url.searchParams.delete('module');
        window.history.replaceState(
          { searchScenario: scenario },
          '',
          `${url.pathname}${url.search}${url.hash}`
        );
      } catch {
        // Reject malformed navigation URLs before changing the scenario or dispatching a request.
        return false;
      }
      this.syncQuickPills(scenario);
      this.closeHistory();
      this.setResultTab('all');
      this.pushEvent('scenario', { scenario, query });
      void runPlaceDiagnosis(this);
      return true;
    }
    initializeResultTabs() {
      this.refreshResultControls();
      const active = this.tabs.find((tab) => tab.getAttribute('aria-selected') === 'true');
      const selectedTab = active?.dataset.searchTab || 'all';
      this.activeResultTab = RESULT_TABS.has(selectedTab) ? selectedTab : 'all';
      this.applyResultTab();
    }
    /** @param {MouseEvent} event */
    onResultTabClick(event) {
      if (!(event.target instanceof Element)) return;
      /** @type {HTMLElement | null} */
      const button = event.target.closest('[data-search-tab]');
      if (button && this.searchSection?.contains(button))
        this.setResultTab(button.dataset.searchTab || 'all');
    }
    /** @param {KeyboardEvent} event */
    onResultTabKeydown(event) {
      if (!(event.target instanceof Element)) return;
      /** @type {HTMLElement | null} */
      const button = event.target.closest('[data-search-tab]');
      if (
        !button ||
        !this.searchSection?.contains(button) ||
        !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)
      )
        return;
      this.refreshResultControls();
      if (!this.tabs.length) return;
      const currentIndex = Math.max(0, this.tabs.indexOf(button));
      let nextIndex = currentIndex;
      if (event.key === 'Home') nextIndex = 0;
      else if (event.key === 'End') nextIndex = this.tabs.length - 1;
      else if (event.key === 'ArrowLeft')
        nextIndex = (currentIndex - 1 + this.tabs.length) % this.tabs.length;
      else if (event.key === 'ArrowRight') nextIndex = (currentIndex + 1) % this.tabs.length;
      const target = this.tabs[nextIndex];
      if (!target) return;
      event.preventDefault();
      this.setResultTab(target.dataset.searchTab || 'all');
      target.focus();
    }
    /** @param {string} value */
    setResultTab(value) {
      this.refreshResultControls();
      const next = RESULT_TABS.has(value) ? value : 'all';
      this.activeResultTab = next;
      this.tabs.forEach((button) => {
        const active = button.dataset.searchTab === next;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-selected', active ? 'true' : 'false');
        button.tabIndex = active ? 0 : -1;
      });
      this.applyResultTab();
      this.pushEvent('result_tab', { tab: next, query: this.searchTerms() });
    }
    applyResultTab() {
      this.refreshResultControls();
      const tab = RESULT_TABS.has(this.activeResultTab) ? this.activeResultTab : 'all';
      const diagnosisAvailable = Boolean(
        this.diagnosisSurface && this.diagnosisSurface.dataset.resultState !== 'idle'
      );
      if (this.diagnosisSurface)
        this.diagnosisSurface.hidden = !(
          diagnosisAvailable &&
          (tab === 'all' || tab === 'diagnosis')
        );
      let visibleItems = 0;
      this.resultItems.forEach((item) => {
        const kind = item.dataset.searchResultKind;
        const visible =
          tab === 'all' ||
          (tab === 'tools' && kind === 'product') ||
          (tab === 'articles' && kind === 'article');
        item.hidden = !visible;
        if (visible) visibleItems += 1;
      });
      if (this.emptyState) this.emptyState.hidden = tab !== 'all' || diagnosisAvailable;
      if (this.resultsHeader) this.resultsHeader.hidden = tab === 'diagnosis' || visibleItems === 0;
      if (this.visibleCount) this.visibleCount.textContent = String(visibleItems);
    }
    /** @param {Event} event */
    onMainInput(event) {
      if (!(event.target instanceof HTMLInputElement)) return;
      super.onInput(event);
      this.keepInSync(event.target.value, event.target);
      if (event.target.value.trim()) this.closeHistory();
      else this.openHistory();
    }
    /** @param {Event} event */
    onExternalInput(event) {
      if (!(event.target instanceof HTMLInputElement)) return;
      this.keepInSync(event.target.value, event.target);
    }
    /** @param {Event} event */
    onFormReset(event) {
      super.onFormReset(event);
      if (!this.shouldResetForm()) return;
      window.requestAnimationFrame(() => {
        this.keepInSync('', this.input);
        this.openHistory();
      });
    }
    onInputFocus() {
      if (window.innerWidth < 750) this.scrollIntoView({ behavior: 'smooth', block: 'center' });
      if (!this.searchTerms()) this.openHistory();
    }
    /** @param {MouseEvent} event */
    onDelegatedClick(event) {
      if (!(event.target instanceof Element) || !this.form || !this.input) return;
      /** @type {HTMLElement | null} */
      const quickPill = event.target.closest('[data-search-quick-mode]');
      if (quickPill && this.form.contains(quickPill)) {
        event.preventDefault();
        this.activateQuickMode(quickPill.dataset.searchQuickMode || '');
        return;
      }
      /** @type {HTMLElement | null} */
      const historyButton = event.target.closest('[data-search-history-value]');
      if (!historyButton || !this.contains(historyButton)) return;
      event.preventDefault();
      const value = normalizeHistoryTerm(historyButton.dataset.searchHistoryValue);
      if (!value) return;
      this.input.value = value;
      this.updateResetState();
      this.keepInSync(value, this.input);
      this.closeHistory();
      this.submitForm();
    }
    /** @param {SubmitEvent} event */
    onSubmit(event) {
      const query = this.searchTerms();

      if (!query) {
        event.preventDefault();
        this.openHistory();
        return;
      }

      event.preventDefault();
      event.stopImmediatePropagation();

      const params = new URLSearchParams(window.location.search || '');
      const submittedIntent = clean(event.submitter?.dataset.searchIntent);
      const requestedIntent =
        submittedIntent ||
        resolvePlatformSearchIntent(
          query,
          params.get('intent') || '',
          params.get('scenario') || ''
        );

      /** @type {SearchContextOptions} */
      let options = placeOptionsForCurrentQuery(query);
      if (requestedIntent === 'content_search') {
        options = { intent: 'content_search' };
      } else if (requestedIntent === 'place_check') {
        options = { ...options, intent: 'place_check' };
      }

      this.pushEvent('submit', { query });

      dispatchCanonicalContext(
        createCanonicalContext(query, this.dataset.searchSource || 'main-search', options)
      );
    }
    searchTerms() {
      return normalizeHistoryTerm(this.input?.value);
    }
    /**
     * @param {string} value
     * @param {HTMLInputElement | null} target
     */
    keepInSync(value, target) {
      this.allSearchInputs.forEach((input) => {
        if (input !== target && input.value !== value) {
          input.value = value;
          input.dispatchEvent(createEvent('change', { bubbles: true }));
        }
      });
    }
    submitForm() {
      const form = this.form;
      if (!form) return;
      form.dataset.searchProgrammaticSubmit = 'true';
      try {
        if (typeof form.requestSubmit === 'function') form.requestSubmit();
        else {
          const submitEvent = createEvent('submit', { bubbles: true, cancelable: true });
          if (form.dispatchEvent(submitEvent)) form.submit();
        }
      } finally {
        window.setTimeout(() => {
          delete form.dataset.searchProgrammaticSubmit;
        }, 0);
      }
    }
    /** @param {unknown} value */
    addHistory(value) {
      this.history = rememberHistory(value);
      this.renderHistory();
    }
    clearHistory() {
      this.history = [];
      safeStorageWrite([]);
      window.dispatchEvent(
        new CustomEvent('littleg:search:history-updated', {
          detail: { key: HISTORY_KEY, items: [] },
        })
      );
      this.renderHistory();
      this.openHistory();
      this.input?.focus();
    }
    renderHistory() {
      const historyList = this.historyList;
      if (!this.historyPanel || !historyList) return;
      historyList.textContent = '';
      /** @type {HTMLElement | null} */
      const empty = this.historyPanel.querySelector('[data-search-history-empty]');
      this.history.forEach((term, index) => {
        const item = document.createElement('li');
        item.id = `search-history-option-${this.dataset.sectionId || 'global'}-${index + 1}`;
        item.setAttribute('role', 'option');
        item.setAttribute('aria-selected', 'false');
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'msearch__history-item';
        button.dataset.searchHistoryValue = term;
        button.tabIndex = -1;
        button.innerHTML = '<span aria-hidden="true">↺</span><span></span>';
        if (button.lastElementChild) button.lastElementChild.textContent = term;
        item.appendChild(button);
        historyList.appendChild(item);
      });
      if (empty) empty.hidden = this.history.length > 0;
      if (this.historyClearButton) this.historyClearButton.hidden = this.history.length === 0;
      this.dispatchEvent(new CustomEvent('main-search:history-updated', { bubbles: true }));
    }
    openHistory() {
      if (!this.historyPanel || this.searchTerms()) return;
      this.historyPanel.hidden = false;
      this.dispatchEvent(new CustomEvent('main-search:history-open', { bubbles: true }));
    }
    closeHistory() {
      if (this.historyPanel) this.historyPanel.hidden = true;
    }
    /**
     * @param {string} action
     * @param {Record<string, unknown>} data
     */
    pushEvent(action, data) {
      if (!this.enableTracking) return;
      try {
        window.dataLayer?.push?.({
          event: 'platform_main_search',
          action,
          section_id: this.dataset.sectionId || '',
          ...(data || {}),
        });
      } catch {
        // Analytics is optional and must not interrupt navigation or search.
      }
    }
    /**
     * @param {string} base
     * @param {Record<string, unknown>} params
     */
    buildUrl(base, params) {
      try {
        const url = new URL(base || '/', window.location.origin);
        Object.entries(params || {}).forEach(([key, value]) => {
          const normalized = clean(value);
          if (normalized) url.searchParams.set(key, normalized);
        });
        return url.pathname + url.search + url.hash;
      } catch {
        // Keep the supplied route if optional query enrichment cannot parse it.
        return base;
      }
    }
  }
  if (!customElements.get('main-search')) customElements.define('main-search', MainSearch);

  class PredictiveSearch extends CoreSearchForm {
    constructor() {
      super();
      /** @type {Record<string, string>} */
      this.cachedResults = {};
      /** @type {HTMLElement | null} */
      this.predictiveSearchResults = this.querySelector(SELECTORS.predictiveResults);
      /** @type {HTMLElement | null} */
      this.historyPanel = this.querySelector(SELECTORS.history);
      /** @type {NodeListOf<PredictiveSearch>} */
      this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
      this.isOpen = false;
      this.abortController = createAbortController();
      this.searchTerm = '';
      this.latestQueryKey = '';
      /** @type {HTMLElement | null} */
      this.statusElement = this.querySelector('.predictive-search-status');
      this.loadingText = this.dataset.loadingText || 'Loading…';
      this.errorText =
        this.dataset.errorText ||
        'Suggestions are temporarily unavailable. Press Search to view the full results.';
      /** @type {number | null} */
      this.changeTimeout = null;
      /** @type {number | null} */
      this.focusOutTimeout = null;
      /** @type {number | null} */
      this.liveRegionTimeout = null;
      /** @type {string | false} */
      this.resultsMaxHeight = false;
      // Presentation IDs only; this randomness is never used for authorization or data access.
      this.instanceId = `predictive-${Math.random().toString(36).slice(2, 9)}`;
      this.setupPredictiveSearch();
    }
    setupPredictiveSearch() {
      if (!this.input) return;
      this.input.form?.addEventListener('submit', this.onFormSubmit.bind(this));
      if (this.controlOwner !== this)
        this.input.form?.addEventListener('reset', this.onFormReset.bind(this));
      this.input.addEventListener('input', this.onChange.bind(this));
      this.input.addEventListener('focus', this.onFocus.bind(this));
      this.addEventListener('focusout', this.onFocusOut.bind(this));
      this.addEventListener('keyup', this.onKeyup.bind(this));
      this.addEventListener('keydown', this.onKeydown.bind(this));
      this.addEventListener('click', this.onClick.bind(this));
      this.addEventListener('main-search:history-open', this.openHistory.bind(this));
      this.addEventListener('main-search:history-updated', () => {
        if (!this.getQuery()) this.openHistory();
      });
    }
    getQuery() {
      return this.input ? this.input.value.trim() : '';
    }
    /** @param {unknown} searchTerm */
    getQueryKey(searchTerm) {
      return clean(searchTerm).replace(/\s+/g, '-').toLowerCase();
    }
    onChange() {
      window.clearTimeout(this.changeTimeout ?? undefined);
      this.changeTimeout = window.setTimeout(() => this.onChangeDebounced(), 250);
    }
    onChangeDebounced() {
      const newSearchTerm = this.getQuery();
      if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm))
        this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
      this.updateSearchForTerm(this.searchTerm, newSearchTerm);
      this.searchTerm = newSearchTerm;
      if (!this.searchTerm.length) {
        this.closeResults();
        this.removeAttribute('results');

        if (this.predictiveSearchResults) {
          this.predictiveSearchResults.innerHTML = '';
        }

        this.openHistory();
        return;
      }
      this.closeHistory();
      this.getSearchResults(this.searchTerm);
    }
    /** @param {SubmitEvent} event */
    onFormSubmit(event) {
      if (
        event.defaultPrevented ||
        (event.currentTarget instanceof HTMLElement &&
          event.currentTarget.dataset.searchProgrammaticSubmit === 'true')
      )
        return;
      /** @type {HTMLElement | null} */
      const selectedAction = this.querySelector(SELECTORS.selectedAction);
      if (!this.getQuery().length) {
        event.preventDefault();
        this.openHistory();
        return;
      }
      if (selectedAction) {
        event.preventDefault();
        selectedAction.click();
      }
    }
    /** @param {Event} event */
    onFormReset(event) {
      super.onFormReset(event);
      if (!this.shouldResetForm()) return;
      window.clearTimeout(this.changeTimeout ?? undefined);
      this.searchTerm = '';
      this.latestQueryKey = '';
      this.abortActiveRequest();
      this.removeAttribute('results');
      if (this.predictiveSearchResults) this.predictiveSearchResults.innerHTML = '';
      this.openHistory();
    }
    onFocus() {
      const current = this.getQuery();
      if (!current) {
        this.openHistory();
        return;
      }
      this.closeHistory();
      if (this.searchTerm !== current) {
        this.searchTerm = current;
        this.getSearchResults(current);
      } else if (this.getAttribute('results') === 'true') this.openResults();
      else this.getSearchResults(current);
    }
    onFocusOut() {
      window.clearTimeout(this.focusOutTimeout ?? undefined);
      this.focusOutTimeout = window.setTimeout(() => {
        if (!this.contains(document.activeElement)) this.close();
      }, 180);
    }
    /** @param {KeyboardEvent} event */
    onKeyup(event) {
      if (event.code === 'ArrowUp') {
        event.preventDefault();
        this.switchOption('up');
      } else if (event.code === 'ArrowDown') {
        event.preventDefault();
        this.switchOption('down');
      } else if (event.code === 'Enter') this.selectOption();
      else if (event.code === 'Escape') this.close();
    }
    /** @param {KeyboardEvent} event */
    onKeydown(event) {
      if (event.code === 'ArrowUp' || event.code === 'ArrowDown') event.preventDefault();
    }
    /** @param {MouseEvent} event */
    onClick(event) {
      if (!(event.target instanceof Element)) return;
      const searchAll = event.target.closest?.(SELECTORS.searchAll);
      if (!searchAll || !this.contains(searchAll)) return;

      event.preventDefault();

      const query = normalizeHistoryTerm(this.getQuery());
      if (!query) return;

      dispatchCanonicalContext(
        createCanonicalContext(query, 'predictive-search-all', {
          intent: 'content_search',
        })
      );
    }
    /**
     * @param {string} previousTerm
     * @param {string} newTerm
     */
    updateSearchForTerm(previousTerm, newTerm) {
      /** @type {HTMLElement | null} */
      const element = this.querySelector(SELECTORS.predictiveSearchText);
      const current = element?.innerText;
      if (!element || !current || !previousTerm) return;
      const pattern = new RegExp(escapeRegExp(previousTerm), 'g');
      const matches = current.match(pattern);
      if (!matches || matches.length <= 1) element.innerText = current.replace(pattern, newTerm);
    }
    getVisibleOptions() {
      /** @type {NodeListOf<HTMLElement>} */
      const options = this.querySelectorAll(SELECTORS.selectable);
      return Array.from(options).filter(
        (element) => element.offsetParent !== null && !element.closest('[hidden]')
      );
    }
    /** @param {string} direction */
    switchOption(direction) {
      if (!this.getAttribute('open') || !this.input) return;
      const options = this.getVisibleOptions();
      if (!options.length) return;
      /** @type {HTMLElement | null} */
      const selected = this.querySelector(SELECTORS.selected);
      const currentIndex = selected ? options.indexOf(selected) : -1;
      let nextIndex;
      if (direction === 'up') nextIndex = currentIndex <= 0 ? options.length - 1 : currentIndex - 1;
      else
        nextIndex = currentIndex < 0 || currentIndex === options.length - 1 ? 0 : currentIndex + 1;
      options.forEach((option, index) =>
        option.setAttribute('aria-selected', index === nextIndex ? 'true' : 'false')
      );
      const active = options[nextIndex];
      if (active.id) this.input.setAttribute('aria-activedescendant', active.id);
      active.scrollIntoView({ block: 'nearest' });
    }
    selectOption() {
      /** @type {HTMLElement | null} */
      const action = this.querySelector(SELECTORS.selectedAction);
      action?.click();
    }
    /** @param {string} searchTerm */
    getSearchResults(searchTerm) {
      const queryKey = this.getQueryKey(searchTerm);
      if (!queryKey) {
        this.openHistory();
        return;
      }
      this.latestQueryKey = queryKey;
      this.clearErrorState();
      this.setLiveRegionLoadingState();
      if (this.cachedResults[queryKey]) {
        this.renderSearchResults(this.cachedResults[queryKey], queryKey);
        return;
      }
      this.abortActiveRequest();
      const requestController = this.abortController;
      const predictiveSearchUrl = window.routes?.predictive_search_url || '/search/suggest';
      const url = `${predictiveSearchUrl}?q=${encodeURIComponent(searchTerm)}&section_id=predictive-search&resources[type]=query,product,collection,article,page&resources[limit]=10`;
      const options = this.abortController ? { signal: this.abortController.signal } : {};
      fetch(url, options)
        .then((response) => {
          if (!response.ok) throw new Error(String(response.status));
          return response.text();
        })
        .then((text) => {
          if (this.latestQueryKey !== queryKey || this.abortController !== requestController)
            return;
          const markup =
            new DOMParser()
              .parseFromString(text, 'text/html')
              .querySelector('#shopify-section-predictive-search')?.innerHTML || '';
          if (!markup.trim()) {
            this.close();
            return;
          }
          this.cacheResults(queryKey, markup);
          this.renderSearchResults(markup, queryKey);
        })
        .catch((error) => {
          if (error?.name === 'AbortError' || error?.code === 20) return;
          if (this.latestQueryKey !== queryKey || this.abortController !== requestController)
            return;
          this.removeAttribute('loading');
          this.removeAttribute('aria-busy');
          this.removeAttribute('open');
          if (this.predictiveSearchResults) {
            this.predictiveSearchResults.removeAttribute('style');
            this.predictiveSearchResults.hidden = true;
          }
          this.input?.setAttribute('aria-expanded', 'false');
          this.isOpen = false;
          this.showErrorState();
        });
    }
    /**
     * @param {string} queryKey
     * @param {string} markup
     */
    cacheResults(queryKey, markup) {
      this.cachedResults[queryKey] = markup;
      this.allPredictiveSearchInstances.forEach((instance) => {
        if (instance?.cachedResults) instance.cachedResults[queryKey] = markup;
      });
    }
    abortActiveRequest() {
      this.abortController?.abort();
      this.abortController = createAbortController();
    }
    clearErrorState() {
      if (this.statusElement?.dataset.state !== 'error') return;
      delete this.statusElement.dataset.state;
      this.statusElement.classList.add('visually-hidden');
      this.statusElement.setAttribute('aria-hidden', 'true');
      this.statusElement.textContent = '';
    }
    showErrorState() {
      if (!this.statusElement) return;
      window.clearTimeout(this.liveRegionTimeout ?? undefined);
      this.statusElement.dataset.state = 'error';
      this.statusElement.classList.remove('visually-hidden');
      this.statusElement.setAttribute('aria-hidden', 'false');
      this.statusElement.textContent = this.errorText;
    }
    setLiveRegionLoadingState() {
      this.clearErrorState();
      this.setLiveRegionText(this.loadingText);
      this.setAttribute('loading', 'true');
      this.setAttribute('aria-busy', 'true');
    }
    /** @param {string} statusText */
    setLiveRegionText(statusText) {
      if (!this.statusElement) return;
      window.clearTimeout(this.liveRegionTimeout ?? undefined);
      this.statusElement.setAttribute('aria-hidden', 'false');
      this.statusElement.textContent = statusText;
      this.liveRegionTimeout = window.setTimeout(
        () => this.statusElement?.setAttribute('aria-hidden', 'true'),
        1000
      );
    }
    normalizeOptionIds() {
      this.querySelectorAll(SELECTORS.selectable).forEach((option, index) => {
        option.id = `${this.instanceId}-option-${index + 1}`;
      });
    }
    /**
     * @param {string} markup
     * @param {string} [queryKey]
     */
    renderSearchResults(markup, queryKey = this.latestQueryKey) {
      if (!this.predictiveSearchResults || queryKey !== this.latestQueryKey) return;
      this.clearErrorState();
      this.predictiveSearchResults.innerHTML = markup;
      this.normalizeOptionIds();
      this.setAttribute('results', 'true');
      this.closeHistory();
      this.setLiveRegionResults();
      this.openResults();
    }
    setLiveRegionResults() {
      this.clearErrorState();
      this.removeAttribute('loading');
      this.removeAttribute('aria-busy');
      const count = this.querySelector(SELECTORS.predictiveCount);
      if (count) this.setLiveRegionText((count.textContent || '').trim());
    }
    getResultsMaxHeight() {
      const header = document.querySelector(
        '[data-module="site-header"][data-shell-surface="context-bar"]'
      );
      const headerBottom = header?.getBoundingClientRect().bottom || 0;
      const height = `${Math.max(window.innerHeight - headerBottom - 20, 160)}px`;
      this.resultsMaxHeight = height;
      return height;
    }
    openResults() {
      if (!this.predictiveSearchResults || !this.input) return;
      this.predictiveSearchResults.style.maxHeight =
        this.resultsMaxHeight || this.getResultsMaxHeight();
      this.predictiveSearchResults.hidden = false;
      this.setAttribute('open', 'true');
      this.input.setAttribute('aria-expanded', 'true');
      this.isOpen = true;
    }
    openHistory() {
      if (!this.historyPanel || !this.input || this.getQuery()) return;
      this.clearErrorState();
      if (this.predictiveSearchResults) this.predictiveSearchResults.hidden = true;
      this.historyPanel.hidden = false;
      this.setAttribute('open', 'true');
      this.input.setAttribute('aria-expanded', 'true');
      this.isOpen = true;
    }
    closeHistory() {
      if (this.historyPanel) this.historyPanel.hidden = true;
    }
    close(clearSearchTerm = false) {
      this.closeResults(clearSearchTerm);
      this.isOpen = false;
    }
    closeResults(clearSearchTerm = false) {
      if (!this.input) return;
      window.clearTimeout(this.changeTimeout ?? undefined);
      this.changeTimeout = null;
      this.abortActiveRequest();
      this.latestQueryKey = '';
      if (clearSearchTerm) {
        this.input.value = '';
        this.searchTerm = '';
        this.latestQueryKey = '';
        this.removeAttribute('results');
        this.clearErrorState();
      }
      this.querySelectorAll(SELECTORS.selected).forEach((selected) =>
        selected.setAttribute('aria-selected', 'false')
      );
      this.input.removeAttribute('aria-activedescendant');
      this.input.setAttribute('aria-expanded', 'false');
      this.removeAttribute('loading');
      this.removeAttribute('aria-busy');
      this.removeAttribute('open');
      this.resultsMaxHeight = false;
      if (this.predictiveSearchResults) {
        this.predictiveSearchResults.removeAttribute('style');
        this.predictiveSearchResults.hidden = true;
      }
      this.closeHistory();
      this.updateResetState();
    }
  }
  if (!customElements.get('predictive-search'))
    customElements.define('predictive-search', PredictiveSearch);

  const placeLocaleFamily = () => {
    const lang = clean(document.documentElement.lang).toLowerCase();
    if (lang.includes('zh-cn') || lang.includes('zh-hans') || lang.includes('zh-sg'))
      return 'zh-CN';
    if (lang.startsWith('zh')) return 'zh-HK';
    return 'en';
  };
  const placeCopy = () => {
    const family = placeLocaleFamily();
    if (family === 'zh-CN')
      return {
        labels: {
          situation: '你正在看的地方',
          assessment: '你现在想了解什么',
          evidence: '我们目前找到的资料',
          missing: '还要再确认',
          action: '下一步可以怎么做',
        },
        scenario: {
          business_location: '这里适不适合开店？',
          home_search: '这里住起来怎么样？',
          planning_change: '这里之后可能有什么变化？',
          place_risk: '这个地方有哪些要留意？',
        },
        questions: {
          PLACE: '这个地方有哪些要留意？',
          AROUND: '这附近怎么样？',
          FIT: '适不适合你的需要？',
          VALUE: '这个位置有哪些条件要留意？',
          CHANGE: '之后可能有什么变化？',
        },
        loading: '正在确认地点并整理可以查到的公开资料…',
        knownLoading: '地点已经确认，正在补充可以查到的资料。',
        evidenceUnavailable: '目前还没有找到可以确认的公开资料。',
        missingLoading: '还有一些资料正在确认。',
        refine: '请补充大厦名称、街道或地区后重新搜索。',
        retry: '请稍后重试。',
        sourceReview: '查看资料日期和适用范围。',
        siteCheck: '如果决定依赖现场情况，请到现场或向专业人士确认。',
        chooseNext: '根据目前找到的资料决定下一步。',
        verifyPrefix: '再确认：',
        sourceLabel: '资料来自：',
        unconfirmedItem: '还有一项资料需要进一步确认。',
        disclosure:
          '这些内容来自公开资料和目前可以确认的信息，只适合帮助你先看清情况；正式估价、测量、规划或其他专业判断仍需专业人士确认。',
        helperTitle: '暂时还不能确定是哪一个地点',
        helperErrorTitle: '暂时无法查看这个地点',
        error: '暂时无法整理这个地点的资料。',
        handoff: {
          PLACE: '在地图继续查看',
          AROUND: '在地图查看附近',
          FIT: '在地图继续查看',
          VALUE: '看看估价前要准备什么',
          CHANGE: '继续查看未来变化',
        },
      };
    if (family === 'zh-HK')
      return {
        labels: {
          situation: '你正在看的地方',
          assessment: '你現在想了解甚麼',
          evidence: '我們目前找到的資料',
          missing: '還要再確認',
          action: '下一步可以怎樣做',
        },
        scenario: {
          business_location: '這裡適不適合開店？',
          home_search: '這裡住起來怎樣？',
          planning_change: '這裡之後可能有甚麼變化？',
          place_risk: '這個地方有哪些要留意？',
        },
        questions: {
          PLACE: '這個地方有哪些要留意？',
          AROUND: '這附近怎樣？',
          FIT: '適不適合你的需要？',
          VALUE: '這個位置有哪些條件要留意？',
          CHANGE: '之後可能有甚麼變化？',
        },
        loading: '正在確認地點並整理可以查到的公開資料…',
        knownLoading: '地點已經確認，正在補充可以查到的資料。',
        evidenceUnavailable: '目前還沒有找到可以確認的公開資料。',
        missingLoading: '還有一些資料正在確認。',
        refine: '請補充大廈名稱、街道或地區後重新搜尋。',
        retry: '請稍後再試。',
        sourceReview: '查看資料日期和適用範圍。',
        siteCheck: '如果決定依賴現場情況，請到現場或向專業人士確認。',
        chooseNext: '根據目前找到的資料決定下一步。',
        verifyPrefix: '再確認：',
        sourceLabel: '資料來自：',
        unconfirmedItem: '還有一項資料需要進一步確認。',
        disclosure:
          '這些內容來自公開資料和目前可以確認的資訊，只適合幫助你先看清情況；正式估價、測量、規劃或其他專業判斷仍需專業人士確認。',
        helperTitle: '暫時還不能確定是哪一個地點',
        helperErrorTitle: '暫時無法查看這個地點',
        error: '暫時無法整理這個地點的資料。',
        handoff: {
          PLACE: '在地圖繼續查看',
          AROUND: '在地圖查看附近',
          FIT: '在地圖繼續查看',
          VALUE: '看看估價前要準備甚麼',
          CHANGE: '繼續查看未來變化',
        },
      };
    return {
      labels: {
        situation: 'The place you are checking',
        assessment: 'What you want to know',
        evidence: 'What we found',
        missing: 'Still worth checking',
        action: 'What you can do next',
      },
      scenario: {
        business_location: 'Could this work for your business?',
        home_search: 'What is it like to live here?',
        planning_change: 'What may change around here?',
        place_risk: 'What should you notice about this place?',
      },
      questions: {
        PLACE: 'What should you notice about this place?',
        AROUND: 'What is nearby?',
        FIT: 'Does it fit what you need?',
        VALUE: 'What conditions are worth considering?',
        CHANGE: 'What may change later?',
      },
      loading: 'Confirming the place and collecting available public information…',
      knownLoading: 'The place is confirmed. We are adding available public information.',
      evidenceUnavailable: 'We have not found public information we can confirm yet.',
      missingLoading: 'A few items are still being checked.',
      refine: 'Add the building name, street or district and search again.',
      retry: 'Try again shortly.',
      sourceReview: 'Check when the information was updated and what area it covers.',
      siteCheck:
        'If your decision depends on what is physically there, check on site or ask a professional.',
      chooseNext: 'Use what we found to decide what to do next.',
      verifyPrefix: 'Check: ',
      sourceLabel: 'From: ',
      unconfirmedItem: 'There is one more item worth checking.',
      disclosure:
        'This uses public information and what can currently be confirmed to help you understand the place. A formal valuation, survey, planning or other professional decision still needs a qualified professional.',
      helperTitle: 'We cannot tell which exact place you mean yet',
      helperErrorTitle: 'This place cannot be checked right now',
      error: 'We cannot gather information for this place right now.',
      handoff: {
        PLACE: 'Keep checking on the map',
        AROUND: 'See what is nearby',
        FIT: 'Keep checking on the map',
        VALUE: 'See what to prepare before a valuation',
        CHANGE: 'Keep checking future changes',
      },
    };
  };

  /**
   * @param {unknown} value
   * @param {string} [fallback]
   */
  const plainConsumerText = (value, fallback = '') => {
    const text = clean(value).slice(0, 240);
    if (!text) return clean(fallback).slice(0, 240);
    if (TECHNICAL_COPY_PATTERN.test(text)) return clean(fallback).slice(0, 240);
    if (/^[A-Z0-9_-]{4,}$/.test(text) || /\b[a-z]+_[a-z0-9_]+\b/i.test(text))
      return clean(fallback).slice(0, 240);
    return text;
  };
  /** @param {unknown} value */
  const consumerTextKey = (value) =>
    clean(value)
      .toLowerCase()
      .replace(/[\s\p{P}\p{S}]+/gu, '')
      .replace(/(?:請|请|再|需要|確認|确认|核實|核实|check|verify)/g, '');
  /**
   * @param {unknown[]} items
   * @param {number} [limit]
   */
  const uniqueConsumerText = (items, limit = 8) => {
    const seen = new Set();
    /** @type {string[]} */
    const output = [];
    (items || []).forEach((value) => {
      const text = clean(value);
      const key = consumerTextKey(text);
      if (!text || !key || seen.has(key) || output.length >= limit) return;
      seen.add(key);
      output.push(text);
    });
    return output;
  };
  /**
   * @param {unknown[]} items
   * @param {number} [limit]
   */
  const uniqueText = (items, limit = 8) =>
    Array.from(new Set((items || []).map(clean).filter(Boolean))).slice(0, limit);
  const createSpatialRequestId = () => {
    let value = '';
    try {
      value = window.crypto?.randomUUID?.().replace(/[^A-Za-z0-9-]/g, '') || '';
    } catch {
      // Crypto support is optional; the correlation-only fallback below is not an authorization token.
    }
    // Correlation-only fallback for environments without crypto.randomUUID; not an authentication identifier.
    if (value.length < 8)
      value = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
    return `REQ-${value.slice(0, 92)}`;
  };
  /** @param {unknown} value */
  const opaqueRequestId = (value) => {
    const requestId = clean(value).slice(0, 100);
    return /^[A-Za-z0-9][A-Za-z0-9._:-]{7,99}$/.test(requestId) ? requestId : '';
  };
  /**
   * @param {Pick<Response, 'status'> | null} response
   * @param {SpatialEnvelope | null} payload
   * @param {unknown} fallbackRequestId
   */
  const normalizeSpatialFailure = (response, payload, fallbackRequestId) => {
    const envelope = payload && typeof payload === 'object' ? payload : {};
    const error = envelope.error && typeof envelope.error === 'object' ? envelope.error : {};
    const status = Number(response?.status) || 0;
    const httpRetryable = status === 408 || status === 425 || status === 429 || status >= 500;
    const retryable = typeof error.retryable === 'boolean' ? error.retryable : httpRetryable;
    return {
      state: 'error',
      code: clean(error.code).slice(0, 80) || (status ? `HTTP_${status}` : 'SPATIAL_FAILURE'),
      retryable,
      action: clean(error.action).slice(0, 40) || (retryable ? 'retry' : 'modify_query'),
      request_id:
        opaqueRequestId(envelope.request_id) ||
        opaqueRequestId(envelope.meta?.request_id) ||
        opaqueRequestId(fallbackRequestId),
    };
  };
  /**
   * @param {unknown} error
   * @param {unknown} fallbackRequestId
   */
  const normalizeSpatialRuntimeFailure = (error, fallbackRequestId) => ({
    state: 'error',
    code:
      error && typeof error === 'object' && 'name' in error && error.name === 'AbortError'
        ? 'REQUEST_TIMEOUT'
        : 'NETWORK_ERROR',
    retryable: true,
    action: 'retry',
    request_id: opaqueRequestId(fallbackRequestId),
  });
  /** @param {SpatialEnvelope | null | undefined} payload */
  const spatialEnvelopeData = (payload) =>
    !payload ||
    typeof payload !== 'object' ||
    payload.success !== true ||
    !payload.data ||
    typeof payload.data !== 'object'
      ? null
      : payload.data;
  /**
   * @param {SpatialData | null | undefined} data
   * @param {string} [fallback]
   */
  const placeLabelFromData = (data, fallback = '') => {
    const place = data?.place && typeof data.place === 'object' ? data.place : {};
    return clean(
      place.formatted_address || place.label || place.name || place.text || fallback
    ).slice(0, 240);
  };
  /** @param {SpatialData | null | undefined} data */
  const placeVerifiedFromData = (data) => {
    const place = data?.place && typeof data.place === 'object' ? data.place : {};
    const resolution =
      data?.place_resolution && typeof data.place_resolution === 'object'
        ? data.place_resolution
        : {};
    return (
      place.verified === true ||
      (clean(place.state) === 'explicit' && clean(resolution.state).includes('verified'))
    );
  };
  /** @param {SpatialData | null | undefined} data */
  const placeCandidatesFromData = (data) => {
    const resolution =
      data?.place_resolution && typeof data.place_resolution === 'object'
        ? data.place_resolution
        : {};
    return (Array.isArray(resolution.candidates) ? resolution.candidates : [])
      .map((candidate) =>
        clean(candidate?.label || candidate?.formatted_address || candidate?.name)
      )
      .filter(Boolean)
      .slice(0, 5);
  };
  /**
   * @param {GapValue} value
   * @param {PlaceCopy} copy
   */
  const normalizeGap = (value, copy) =>
    value && typeof value === 'object'
      ? plainConsumerText(value.message || value.label, copy.unconfirmedItem)
      : plainConsumerText(value, copy.unconfirmedItem);
  /** @param {StructuredResult} structured */
  const safeMarketContext = (structured) => {
    const market =
      structured?.market_context && typeof structured.market_context === 'object'
        ? structured.market_context
        : null;
    if (
      market?.market_context_ready !== true ||
      market.valuation_ready !== false ||
      market.opinion_of_value !== false
    )
      return [];
    return (Array.isArray(market.items) ? market.items : [])
      .slice(0, 3)
      .map((item) => {
        const locale = placeLocaleFamily();
        let fallback = 'Official market information';
        if (locale === 'zh-CN') fallback = '官方市场资料';
        else if (locale !== 'en') fallback = '官方市場資料';
        const label = plainConsumerText(item?.label || item?.name, fallback);
        const value = Number.isFinite(Number(item?.value))
          ? String(item.value)
          : plainConsumerText(item?.value, '');
        const period = plainConsumerText(item?.period || market.latest_period, '');
        const providerName = plainConsumerText(item?.provider_name || item?.provider, '');
        return [label, value, period, providerName].filter(Boolean).join(' · ');
      })
      .filter(Boolean);
  };
  /**
   * @param {unknown} scenario
   * @param {unknown} consumerJob
   */
  const scenarioQuestion = (scenario, consumerJob) => {
    const copy = placeCopy();
    const profile = scenarioProfile(scenario);
    const key = presentationKeyForConsumerJob(consumerJob);
    if (profile) return copy.scenario[profile.key] || copy.questions[key] || copy.questions.PLACE;
    return copy.questions[key] || copy.questions.PLACE;
  };
  /**
   * @param {StructuredResult['fit_response']} fit
   * @param {string} scenario
   * @param {Set<string>} verifiedSummaries
   * @param {string[]} known
   * @param {string[]} missing
   * @param {string[]} recommendedSteps
   * @param {PlaceCopy} copy
   */
  const appendFitEvidence = (
    fit,
    scenario,
    verifiedSummaries,
    known,
    missing,
    recommendedSteps,
    copy
  ) => {
    if (!fit || typeof fit !== 'object') return;
    const fitScenario = normalizeScenario(fit?.scenario || fit?.surface_mode);
    if (
      clean(fit.contract) !== PLACE_FIT_RESPONSE_CONTRACT ||
      (fitScenario && fitScenario !== scenario)
    )
      return;
    (Array.isArray(fit.known_facts) ? fit.known_facts : []).forEach((value) => {
      const text = plainConsumerText(value, '');
      if (text && verifiedSummaries.has(text)) known.push(text);
    });
    (Array.isArray(fit.missing_blindspots) ? fit.missing_blindspots : []).forEach((value) => {
      const text = plainConsumerText(value, copy.unconfirmedItem);
      if (text) missing.push(text);
    });
    (Array.isArray(fit.actionable_next_steps) ? fit.actionable_next_steps : []).forEach((value) => {
      const text = plainConsumerText(value, '');
      if (text) recommendedSteps.push(text);
    });
  };

  /**
   * @param {SpatialData | null | undefined} data
   * @param {string} consumerJob
   * @param {string} query
   * @param {string} phase
   * @param {string} [scenario]
   */
  const projectPlaceEvidence = (data, consumerJob, query, phase, scenario = '') => {
    const copy = placeCopy();
    const normalizedScenario = normalizeScenario(scenario);
    /** @type {string[]} */
    const known = [];
    /** @type {string[]} */
    const missing = [];
    /** @type {string[]} */
    const sourceLabels = [];
    /** @type {string[]} */
    const recommendedSteps = [];
    const referenced = new Set();
    const structured =
      data?.structured_result && typeof data.structured_result === 'object'
        ? data.structured_result
        : {};
    const evidence =
      structured.evidence && typeof structured.evidence === 'object' ? structured.evidence : {};
    const findings = Array.isArray(evidence.findings) ? evidence.findings : [];
    const sources = Array.isArray(evidence.sources) ? evidence.sources : [];
    const verifiedSummaries = new Set();
    findings.slice(0, 8).forEach((finding) => {
      if (clean(finding?.state).toLowerCase() !== 'verified') return;
      const summary = plainConsumerText(finding?.summary, '');
      if (!summary) return;
      verifiedSummaries.add(summary);
      known.push(summary);
      (Array.isArray(finding?.source_ids) ? finding.source_ids : []).forEach((id) => {
        const sourceId = clean(id);
        if (sourceId) referenced.add(sourceId);
      });
    });
    appendFitEvidence(
      structured.fit_response,
      normalizedScenario,
      verifiedSummaries,
      known,
      missing,
      recommendedSteps,
      copy
    );
    sources.forEach((source) => {
      if (referenced.size && !referenced.has(clean(source?.id))) return;
      const providerName = plainConsumerText(source?.provider_name || source?.provider, '');
      const date = plainConsumerText(
        source?.as_of || source?.checked_at || source?.retrieved_at,
        ''
      );
      const label = [providerName, date].filter(Boolean).join(' · ');
      if (label) sourceLabels.push(label);
    });
    (Array.isArray(evidence.missing) ? evidence.missing : []).forEach((gapValue) => {
      const gap = normalizeGap(gapValue, copy);
      if (gap) missing.push(gap);
    });
    (Array.isArray(structured.missing_inputs) ? structured.missing_inputs : []).forEach(
      (gapValue) => {
        const gap = normalizeGap(gapValue, copy);
        if (gap) missing.push(gap);
      }
    );
    (Array.isArray(structured.readiness?.missing_inputs)
      ? structured.readiness.missing_inputs
      : []
    ).forEach((gapValue) => {
      const gap = normalizeGap(gapValue, copy);
      if (gap) missing.push(gap);
    });
    const recommended = normalizeGap(
      structured.recommended_next_step || structured.readiness?.recommended_next_step,
      copy
    );
    if (recommended) recommendedSteps.push(recommended);
    known.push(...safeMarketContext(structured));
    const finalKnown = uniqueConsumerText(known, 8);
    const finalMissing = uniqueConsumerText(missing, 3);
    const actions = uniqueConsumerText(recommendedSteps, 5);
    finalMissing.forEach((gap) => {
      if (actions.length < 5) actions.push(`${copy.verifyPrefix}${gap}`);
    });
    [copy.sourceReview, copy.siteCheck, copy.chooseNext].forEach((action) => {
      if (actions.length < 3) actions.push(action);
    });
    const sourceLine = uniqueConsumerText(sourceLabels, 4);
    const sourceSummary = sourceLine.length ? [`${copy.sourceLabel}${sourceLine.join(' · ')}`] : [];
    const knownWithSources = finalKnown.length ? finalKnown.concat(sourceSummary) : [];
    return {
      place: placeLabelFromData(data, query),
      known: uniqueConsumerText(knownWithSources, 8),
      missing: finalMissing,
      actions: uniqueConsumerText(actions, 5),
      phase,
      consumerJob,
      evidenceState: clean(data?.evidence_state),
    };
  };

  /**
   * @param {HTMLElement | null} rows
   * @param {ResultModule} key
   * @param {string} label
   * @param {string | string[]} value
   */
  const appendPlaceResultModule = (rows, key, label, value) => {
    if (!rows || !PLACE_RESULT_MODULES.includes(key)) return;
    const group = document.createElement('div');
    const dt = document.createElement('dt');
    const dd = document.createElement('dd');
    group.dataset.resultModule = key;
    dt.textContent = label;
    if (Array.isArray(value)) {
      let limit = 8;
      if (key === 'missing') limit = 3;
      else if (key === 'action') limit = 5;
      const items = uniqueConsumerText(value, limit);
      if (items.length) {
        const list = document.createElement(key === 'action' ? 'ol' : 'ul');
        list.dataset.resultList = key;
        items.forEach((text) => {
          const item = document.createElement('li');
          item.textContent = text;
          list.appendChild(item);
        });
        dd.appendChild(list);
      } else dd.textContent = '—';
    } else dd.textContent = clean(value) || '—';
    group.append(dt, dd);
    rows.appendChild(group);
  };
  /**
   * @param {string} path
   * @param {Record<string, unknown>} params
   * @param {string} placeLabel
   * @param {string} query
   */
  const buildPublishedHandoff = (path, params, placeLabel, query) => {
    const url = new URL(localizedPath(path), window.location.origin);
    Object.entries(params || {}).forEach(([key, value]) => {
      const normalized = clean(value);
      if (normalized) url.searchParams.set(key, normalized);
    });
    if (clean(placeLabel)) url.searchParams.set('place_label', clean(placeLabel).slice(0, 180));
    if (clean(query)) url.searchParams.set('q', normalizeHistoryTerm(query));
    return url.pathname + url.search;
  };
  /**
   * @param {unknown} consumerJob
   * @param {string} [placeLabel]
   * @param {string} [query]
   */
  const placeHandoffForConsumerJob = (consumerJob, placeLabel = '', query = '') => {
    const copy = placeCopy();
    const key = presentationKeyForConsumerJob(consumerJob);
    const routes = {
      PLACE: {
        href: buildPublishedHandoff(
          '/pages/gproperty-map',
          { owner: 'map', workspace: 'map', intent: 'explore_space' },
          placeLabel,
          query
        ),
        label: copy.handoff.PLACE,
      },
      AROUND: {
        href: buildPublishedHandoff(
          '/pages/gproperty-map',
          { owner: 'projects', workspace: 'properties', intent: 'find_property' },
          placeLabel,
          query
        ),
        label: copy.handoff.AROUND,
      },
      FIT: {
        href: buildPublishedHandoff(
          '/pages/gproperty-map',
          { owner: 'projects', workspace: 'properties', intent: 'find_property' },
          placeLabel,
          query
        ),
        label: copy.handoff.FIT,
      },
      VALUE: {
        href: buildPublishedHandoff(
          '/products/glg-spatial-valuation-preview',
          { source: 'place-check' },
          placeLabel,
          query
        ),
        label: copy.handoff.VALUE,
      },
      CHANGE: {
        href: buildPublishedHandoff(
          '/pages/gcitypartners',
          { owner: 'tools', workspace: 'professional', intent: 'professional_tools' },
          placeLabel,
          query
        ),
        label: copy.handoff.CHANGE,
      },
    };
    return routes[key] || routes.PLACE;
  };
  /** @param {HTMLElement | null} result */
  const diagnosisParts = (result) => ({
    ready: /** @type {HTMLElement | null | undefined} */ (
      result?.querySelector('[data-diagnosis-ready]')
    ),
    helper: /** @type {HTMLElement | null | undefined} */ (
      result?.querySelector('[data-diagnosis-helper]')
    ),
    helperTitle: /** @type {HTMLElement | null | undefined} */ (
      result?.querySelector('[data-diagnosis-helper-title]')
    ),
    helperText: /** @type {HTMLElement | null | undefined} */ (
      result?.querySelector('[data-diagnosis-helper-text]')
    ),
    candidates: /** @type {HTMLElement | null | undefined} */ (
      result?.querySelector('[data-diagnosis-candidates]')
    ),
  });
  /** @param {HTMLElement} result */
  const showDiagnosisReady = (result) => {
    const parts = diagnosisParts(result);
    if (parts.ready) parts.ready.hidden = false;
    if (parts.helper) parts.helper.hidden = true;
    if (parts.candidates) {
      parts.candidates.textContent = '';
      parts.candidates.hidden = true;
    }
  };
  /**
   * @param {SearchOwner} owner
   * @param {HTMLElement} result
   * @param {string} state
   * @param {string} title
   * @param {string} text
   * @param {unknown[]} [candidates]
   */
  const showDiagnosisHelper = (owner, result, state, title, text, candidates = []) => {
    const parts = diagnosisParts(result);
    if (parts.ready) parts.ready.hidden = true;
    if (parts.helper) parts.helper.hidden = false;
    if (parts.helperTitle) parts.helperTitle.textContent = title;
    if (parts.helperText) parts.helperText.textContent = text;
    const candidateList = parts.candidates;
    if (candidateList) {
      candidateList.textContent = '';
      uniqueText(candidates, 5).forEach((value) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'msearch__diagnosis-candidate';
        button.dataset.diagnosisCandidate = value;
        button.textContent = value;
        candidateList.appendChild(button);
      });
      candidateList.hidden = candidateList.childElementCount === 0;
    }
    result.dataset.resultState = state;
    result.setAttribute('aria-busy', 'false');
    result.hidden = false;
    if (owner.activeResultTab === 'diagnosis') owner.setResultTab?.('all');
    else owner.applyResultTab?.();
  };
  /**
   * @param {HTMLElement} rows
   * @param {PlaceCopy} copy
   * @param {PlaceProjection} projection
   * @param {string} phase
   * @param {string} query
   * @param {string} scenario
   * @param {string} consumerJob
   */
  const appendProjectedFacets = (rows, copy, projection, phase, query, scenario, consumerJob) => {
    const knownFallback = phase === 'defer' ? copy.knownLoading : copy.evidenceUnavailable;
    const knownValue = projection.known.length ? projection.known : [knownFallback];
    const missingValue =
      phase === 'defer' && !projection.missing.length ? [copy.missingLoading] : projection.missing;
    const values = {
      situation: projection.place || query,
      assessment: scenarioQuestion(scenario, consumerJob),
      evidence: knownValue,
      missing: missingValue,
      action: projection.actions,
    };
    PLACE_RESULT_MODULES.forEach((key) =>
      appendPlaceResultModule(rows, key, copy.labels[key], values[key])
    );
  };
  /**
   * @param {SearchOwner} owner
   * @param {string} query
   * @param {string} consumerJob
   * @param {SpatialData} data
   * @param {string} [phase]
   * @param {string} [scenario]
   */
  const renderPlaceResult = (owner, query, consumerJob, data, phase = 'include', scenario = '') => {
    /** @type {HTMLElement | null} */
    const section = owner.closest('[data-module="main-search"]');
    /** @type {HTMLElement | null | undefined} */
    const result = section?.querySelector('[data-need-result]');
    /** @type {HTMLElement | null | undefined} */
    const rows = result?.querySelector('[data-need-result-rows]');
    if (!result || !rows) return;
    const copy = placeCopy();
    const projection = projectPlaceEvidence(data, consumerJob, query, phase, scenario);
    const verified = placeVerifiedFromData(data);
    const candidates = placeCandidatesFromData(data);
    const handoff = placeHandoffForConsumerJob(consumerJob, projection.place || query, query);
    /** @type {HTMLElement | null} */
    const question = result.querySelector('[data-need-result-question]');
    /** @type {HTMLElement | null} */
    const disclosure = result.querySelector('[data-need-result-disclosure]');
    /** @type {HTMLAnchorElement | null} */
    const cta = result.querySelector('[data-need-result-cta]');
    /** @type {HTMLElement | null} */
    const ctaLabel = result.querySelector('[data-need-result-cta-label]');
    rows.textContent = '';
    result.dataset.surfaceMode = SEARCH_SURFACE_MODE;
    if (scenario) result.dataset.scenario = scenario;
    else delete result.dataset.scenario;
    delete result.dataset.errorRetryable;
    delete result.dataset.evidenceState;
    if (!verified) {
      showDiagnosisHelper(owner, result, 'clarify', copy.helperTitle, copy.refine, candidates);
      return;
    }
    showDiagnosisReady(result);
    if (question)
      question.textContent = `${projection.place || query} · ${scenarioQuestion(scenario, consumerJob)}`;
    if (disclosure) disclosure.textContent = phase === 'defer' ? copy.loading : copy.disclosure;
    appendProjectedFacets(rows, copy, projection, phase, query, scenario, consumerJob);
    if (cta && ctaLabel) {
      cta.href = handoff.href;
      ctaLabel.textContent = handoff.label;
      cta.hidden = false;
      cta.setAttribute('aria-disabled', 'false');
    }
    result.dataset.resultState = phase === 'defer' ? 'loading-evidence' : 'ready';
    result.setAttribute('aria-busy', phase === 'defer' ? 'true' : 'false');
    result.hidden = false;
    owner.applyResultTab?.();
  };
  /**
   * @param {SearchOwner} owner
   * @param {string} query
   * @param {string} consumerJob
   * @param {string} [scenario]
   * @param {{retryable?: boolean} | null} [failure]
   */
  const renderPlaceResultError = (owner, query, consumerJob, scenario = '', failure = null) => {
    /** @type {HTMLElement | null | undefined} */
    const result = owner
      .closest('[data-module="main-search"]')
      ?.querySelector('[data-need-result]');
    if (!result) return;
    const copy = placeCopy();
    const retryable = failure?.retryable === true;
    const safeTitle = retryable ? copy.helperErrorTitle : copy.helperTitle;
    const safeAction = retryable ? copy.retry : copy.refine;
    result.dataset.surfaceMode = SEARCH_SURFACE_MODE;
    if (scenario) result.dataset.scenario = scenario;
    result.setAttribute('data-error-retryable', retryable ? 'true' : 'false');
    showDiagnosisHelper(owner, result, 'error', safeTitle, `${copy.error} ${safeAction}`, []);
  };
  /**
   * @param {SearchOwner} owner
   * @param {{retryable?: boolean}} failure
   */
  const preservePlaceResultOnEvidenceFailure = (owner, failure) => {
    /** @type {HTMLElement | null | undefined} */
    const result = owner
      .closest('[data-module="main-search"]')
      ?.querySelector('[data-need-result]');
    if (!result || result.hidden) return false;
    const copy = placeCopy();
    /** @type {HTMLElement | null} */
    const disclosure = result.querySelector('[data-need-result-disclosure]');
    result.dataset.resultState = 'ready';
    result.setAttribute('aria-busy', 'false');
    result.setAttribute('data-evidence-state', 'unavailable');
    result.dataset.errorRetryable = failure?.retryable === true ? 'true' : 'false';
    if (disclosure) disclosure.textContent = copy.evidenceUnavailable;
    owner.applyResultTab?.();
    return true;
  };

  /** @param {SearchOwner} owner */
  const placeResultControllers = (owner) => {
    if (!owner.__littlegPlaceResultControllers) owner.__littlegPlaceResultControllers = new Set();
    return owner.__littlegPlaceResultControllers;
  };
  /** @param {SearchOwner} owner */
  const abortPlaceResultRequests = (owner) => {
    const controllers = owner?.__littlegPlaceResultControllers;
    if (!controllers) return;
    controllers.forEach((controller) => {
      try {
        controller?.abort();
      } catch {
        // Continue cleanup even when one optional request abort throws.
      }
    });
    controllers.clear();
  };
  /**
   * @param {SearchOwner} owner
   * @param {string} query
   * @param {string} consumerJob
   * @param {string} evidenceMode
   * @param {PlaceContext | null} placeContext
   * @param {string} token
   * @param {string} [scenario]
   */
  const requestPlaceSpatial = async (
    owner,
    query,
    consumerJob,
    evidenceMode,
    placeContext,
    token,
    scenario = ''
  ) => {
    const requestId = createSpatialRequestId();
    if (typeof window.fetch !== 'function')
      return normalizeSpatialRuntimeFailure(new Error('fetch_unavailable'), requestId);
    const controller = createAbortController();
    if (controller) placeResultControllers(owner).add(controller);
    const timeout = window.setTimeout(() => controller?.abort(), PLACE_REQUEST_TIMEOUT_MS);
    /** @type {{request_id: string, contract: string, input: string, locale: string, surface: string, evidence_mode: string, consumer_job?: string, scenario?: string, surface_mode?: string, place_context?: PlaceContext}} */
    const body = {
      request_id: requestId,
      contract: SPATIAL_API_CONTRACT.request_contract,
      input: query,
      locale: clean(document.documentElement.lang),
      surface: 'littleg_place_diagnosis',
      evidence_mode: evidenceMode,
    };
    const requestedConsumerJob = normalizeConsumerJob(consumerJob);
    if (requestedConsumerJob) body.consumer_job = requestedConsumerJob;
    const normalizedScenario = normalizeScenario(scenario);
    if (normalizedScenario) {
      body.scenario = normalizedScenario;
      body.surface_mode = normalizedScenario;
    }
    if (placeContext?.items?.length) body.place_context = placeContext;
    try {
      try {
        const response = await window.fetch(SPATIAL_API_CONTRACT.canonical_endpoint, {
          method: SPATIAL_API_CONTRACT.method,
          credentials: 'omit',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          signal: controller?.signal,
          body: JSON.stringify(body),
        });
        const payload = await response.json().catch(() => null);
        if (!response.ok) return normalizeSpatialFailure(response, payload, requestId);
        const data = spatialEnvelopeData(payload);
        if (!data)
          return {
            state: 'error',
            code: 'INVALID_RESPONSE',
            retryable: true,
            action: 'retry',
            request_id: opaqueRequestId(requestId),
          };
        if (opaqueRequestId(data.request_id) !== opaqueRequestId(requestId)) {
          return {
            state: 'error',
            code: 'REQUEST_TRACE_MISMATCH',
            retryable: true,
            action: 'retry',
            request_id: opaqueRequestId(requestId),
          };
        }
        if (owner.__littlegPlaceResultToken !== token) return { state: 'stale' };
        return data;
      } catch (error) {
        return normalizeSpatialRuntimeFailure(error, requestId);
      }
    } finally {
      window.clearTimeout(timeout);
      if (controller) placeResultControllers(owner).delete(controller);
    }
  };

  /** @param {SearchOwner} owner */
  const runPlaceDiagnosis = async (owner) => {
    const params = new URLSearchParams(window.location.search || '');
    const query = normalizeHistoryTerm(params.get('q'));
    const scenario =
      normalizeScenario(params.get('scenario')) || normalizeScenario(params.get('surface_mode'));
    const profile = scenarioProfile(scenario);
    const requestedConsumerJob = normalizeConsumerJob(profile?.consumer_job);
    if (!query || owner.dataset.resultWorkspaceContract !== PLACE_RESULT_WORKSPACE_CONTRACT) return;
    /** @type {HTMLElement | null | undefined} */
    const result = owner
      .closest('[data-module="main-search"]')
      ?.querySelector('[data-need-result]');
    if (!result) return;

    abortPlaceResultRequests(owner);
    // Local stale-response marker only; this token never authenticates a request.
    const token = `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
    owner.__littlegPlaceResultToken = token;
    showDiagnosisReady(result);
    result.hidden = false;
    result.dataset.resultState = 'loading';
    result.dataset.surfaceMode = SEARCH_SURFACE_MODE;
    if (scenario) result.dataset.scenario = scenario;
    result.setAttribute('aria-busy', 'true');
    const copy = placeCopy();
    /** @type {HTMLElement | null} */
    const question = result.querySelector('[data-need-result-question]');
    /** @type {HTMLElement | null} */
    const disclosure = result.querySelector('[data-need-result-disclosure]');
    if (question)
      question.textContent = `${query} · ${scenarioQuestion(scenario, requestedConsumerJob)}`;
    if (disclosure) disclosure.textContent = copy.loading;
    owner.syncQuickPills?.(scenario);
    owner.applyResultTab?.();

    let stageAReady = false;
    try {
      const stageA = await requestPlaceSpatial(
        owner,
        query,
        requestedConsumerJob,
        'defer',
        null,
        token,
        scenario
      );
      if (owner.__littlegPlaceResultToken !== token || stageA?.state === 'stale') return;
      if (stageA?.state === 'error') {
        renderPlaceResultError(
          owner,
          query,
          requestedConsumerJob || 'understand_place',
          scenario,
          stageA
        );
        return;
      }
      const resolvedConsumerJob =
        consumerJobFromData(stageA) || requestedConsumerJob || 'understand_place';
      renderPlaceResult(owner, query, resolvedConsumerJob, stageA, 'defer', scenario);
      stageAReady = true;
      if (!placeVerifiedFromData(stageA)) return;
      const placeLabel = placeLabelFromData(stageA, query);
      const placeContext = {
        state: 'single',
        items: [{ text: placeLabel, state: 'explicit', source: 'stage_a_verified_place' }],
      };
      const stageB = await requestPlaceSpatial(
        owner,
        query,
        resolvedConsumerJob,
        'include',
        placeContext,
        token,
        scenario
      );
      if (owner.__littlegPlaceResultToken !== token || stageB?.state === 'stale') return;
      if (stageB?.state === 'error') {
        preservePlaceResultOnEvidenceFailure(owner, stageB);
        return;
      }
      renderPlaceResult(
        owner,
        query,
        consumerJobFromData(stageB) || resolvedConsumerJob,
        stageB,
        'include',
        scenario
      );
    } catch (error) {
      if (owner.__littlegPlaceResultToken !== token) return;
      const failure = normalizeSpatialRuntimeFailure(error, '');
      if (stageAReady) {
        preservePlaceResultOnEvidenceFailure(owner, failure);
        return;
      }
      renderPlaceResultError(
        owner,
        query,
        requestedConsumerJob || 'understand_place',
        scenario,
        failure
      );
    }
  };

  /**
   * @param {string} path
   * @param {Record<string, unknown>} [params]
   */
  const buildPlatformSearchDestination = (path, params = {}) => {
    const absolute = /^https:\/\//i.test(path);
    const url = new URL(absolute ? path : localizedPath(path), window.location.origin);
    Object.entries(params).forEach(([key, value]) => {
      const normalized = clean(value);
      if (normalized) url.searchParams.set(key, normalized);
    });
    return absolute ? url.href : `${url.pathname}${url.search}${url.hash}`;
  };
  /**
   * @param {unknown} query
   * @param {string | null} [explicitIntent]
   * @param {unknown} [scenario]
   */
  const resolvePlatformSearchIntent = (query, explicitIntent = '', scenario = '') => {
    const normalized = clean(explicitIntent).toLowerCase();

    if (normalized === 'content_search') {
      return 'content_search';
    }

    if (normalizeScenario(scenario)) {
      return 'place_check';
    }

    return PLATFORM_SEARCH_INTENTS.has(normalized) ? normalized : 'content_search';
  };
  /**
   * @param {string} intent
   * @param {string} query
   */
  const platformSearchRoute = (intent, query) => {
    const source = 'platform-search';
    if (intent === 'opportunity_search')
      return {
        mechanism: SEARCH_MECHANISMS.backend_authority,
        href: buildPlatformSearchDestination('/pages/gtender', { q: query, view: 'list', source }),
      };
    if (intent === 'provider_search')
      return {
        mechanism: SEARCH_MECHANISMS.backend_authority,
        href: buildPlatformSearchDestination('/pages/gcitypartners-dashboard', {
          workspace: 'partners',
          capabilities: query,
          source,
        }),
      };
    if (intent === 'project_search')
      return {
        mechanism: SEARCH_MECHANISMS.launcher_utility,
        href: buildPlatformSearchDestination('/pages/gcitypartners-dashboard', {
          workspace: 'projects',
          q: query,
          source,
        }),
      };
    if (intent === 'family_explore')
      return {
        mechanism: SEARCH_MECHANISMS.backend_authority,
        href: buildPlatformSearchDestination('/pages/littleg-map', { q: query, intent, source }),
      };
    if (intent === 'location_search')
      return {
        mechanism: SEARCH_MECHANISMS.backend_authority,
        href: buildPlatformSearchDestination('/pages/gproperty-map', { q: query, intent, source }),
      };
    if (intent === 'explore_space')
      return {
        mechanism: SEARCH_MECHANISMS.launcher_utility,
        href: buildPlatformSearchDestination('/pages/gproperty-map', { q: query, source }),
      };
    if (intent === 'tool_execution')
      return {
        mechanism: SEARCH_MECHANISMS.launcher_utility,
        href: buildPlatformSearchDestination('/pages/gcitypartners', {
          q: query,
          intent: 'professional_tools',
          source,
        }),
      };
    if (intent === 'content_search')
      return { mechanism: SEARCH_MECHANISMS.product_content, href: '' };
    return { mechanism: SEARCH_MECHANISMS.ai_qna, href: '' };
  };
  /** @param {SearchOwner} owner */
  const dispatchPlatformSearchResult = (owner) => {
    const params = new URLSearchParams(window.location.search || '');
    const query = normalizeHistoryTerm(params.get('q'));
    if (!query) return false;
    const scenario = normalizeScenario(params.get('scenario'));
    const intent = resolvePlatformSearchIntent(query, params.get('intent'), scenario);
    const route = platformSearchRoute(intent, query);
    owner.dataset.searchMechanism = route.mechanism;
    owner.dataset.searchIntent = intent;

    if (intent === 'place_check') {
      void runPlaceDiagnosis(owner);
      return true;
    }

    abortPlaceResultRequests(owner);
    owner.__littlegPlaceResultToken = '';
    /** @type {HTMLElement | null | undefined} */
    const result = owner
      .closest('[data-module="main-search"]')
      ?.querySelector('[data-need-result]');
    if (result) {
      result.dataset.resultState = 'idle';
      result.hidden = true;
      result.setAttribute('aria-busy', 'false');
    }
    owner.applyResultTab?.();

    window.dispatchEvent(
      new CustomEvent('littleg:search:mechanism-dispatch', {
        detail: Object.freeze({
          query,
          intent,
          mechanism: route.mechanism,
          destination: route.href || '',
        }),
      })
    );

    if (route.href) window.location.assign(route.href);
    return true;
  };

  /** @param {Document | Element} [scope] */
  const bindPlaceResultWorkspace = (scope = document) => {
    /** @type {SearchOwner[]} */
    const owners = [];
    if (
      scope instanceof Element &&
      scope.matches(
        `main-search[data-result-workspace-contract="${PLACE_RESULT_WORKSPACE_CONTRACT}"]`
      )
    )
      owners.push(/** @type {SearchOwner} */ (scope));
    /** @type {NodeListOf<SearchOwner>} */
    const matches = scope.querySelectorAll(
      `main-search[data-result-workspace-contract="${PLACE_RESULT_WORKSPACE_CONTRACT}"]`
    );
    matches.forEach((owner) => owners.push(owner));
    owners.forEach((owner) => {
      if (owner.__littlegPlaceResultBound) return;
      owner.__littlegPlaceResultBound = true;
      /** @type {HTMLElement | null | undefined} */
      const result = owner
        .closest('[data-module="main-search"]')
        ?.querySelector('[data-need-result]');
      /** @type {HTMLInputElement | null} */
      const input = owner.querySelector('input[type="search"]');
      owner.syncQuickPills?.(currentScenario());
      result?.addEventListener('click', (event) => {
        if (!(event.target instanceof Element)) return;
        const candidate = event.target.closest?.('[data-diagnosis-candidate]');
        if (candidate && result.contains(candidate)) {
          event.preventDefault();
          const value = normalizeHistoryTerm(candidate.getAttribute('data-diagnosis-candidate'));
          if (!value || !input) return;
          input.value = value;
          input.dispatchEvent(createEvent('input', { bubbles: true }));
          owner.submitForm?.();
          return;
        }
        const refine = event.target.closest?.('[data-diagnosis-refine]');
        if (refine && result.contains(refine)) {
          event.preventDefault();
          input?.scrollIntoView({ behavior: 'smooth', block: 'center' });
          input?.focus({ preventScroll: true });
          input?.select?.();
        }
      });
      result?.querySelector('[data-need-result-close]')?.addEventListener('click', () => {
        abortPlaceResultRequests(owner);
        owner.__littlegPlaceResultToken = '';
        result.hidden = true;
        result.dataset.resultState = 'idle';
        result.setAttribute('aria-busy', 'false');
        owner.applyResultTab?.();
        input?.focus();
      });
      dispatchPlatformSearchResult(owner);
    });
  };
  /** @param {Document | Element} [scope] */
  const destroyPlaceResultWorkspace = (scope = document) => {
    /** @type {NodeListOf<SearchOwner>} */
    const matches = scope.querySelectorAll(
      `main-search[data-result-workspace-contract="${PLACE_RESULT_WORKSPACE_CONTRACT}"]`
    );
    matches.forEach((owner) => {
      abortPlaceResultRequests(owner);
      owner.__littlegPlaceResultToken = '';
    });
  };
  const bootPlaceResultWorkspace = () => bindPlaceResultWorkspace(document);

  bindGlobalShortcut();
  if (document.readyState === 'loading')
    document.addEventListener('DOMContentLoaded', bootPlaceResultWorkspace, { once: true });
  else bootPlaceResultWorkspace();
  document.addEventListener('shopify:section:load', (event) =>
    bindPlaceResultWorkspace(event.target instanceof Element ? event.target : document)
  );
  document.addEventListener('shopify:section:unload', (event) =>
    destroyPlaceResultWorkspace(event.target instanceof Element ? event.target : document)
  );
})(window);
